import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useContext,
  createContext,
  type KeyboardEvent,
  type DragEvent,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import type { Channel, Message, Member, Reaction, LinkEmbed, ServerEmoji } from '../types';
import { useMessages, useMembers, useTypingUsers, useServerEmojis } from '../hooks';
import { AnimatedGif, isGifLike } from './AnimatedGif';
import { api, ApiError } from '../api';
import { useApp } from '../store';
import { hasPermission } from '../permissions';
import { Button, Spinner, Avatar, Icon } from './ui';
import { UserClickTarget } from './UserPopover';
import { ContextMenu, useContextMenu, type ContextMenuItem } from './ContextMenu';
import { EmojiPicker } from './EmojiPicker';
import { GifPicker } from './GifPicker';
import { wsClient } from '../ws';
import { playSound } from '../sounds';
import { toast } from './Toast';
import { ConfirmDialog } from './ConfirmDialog';
import { isMention } from '../mentions';
import { EMOJI_CATEGORIES } from '../emoji';
import { startCall } from '../calls';
import { loadDraft, saveDraft, clearDraft } from '../drafts';
import { useSearchParams } from 'react-router';
import { pathToShareLink } from '../deeplink';

// =============================================================================
// ServerEmojiContext — current server's custom emoji, available to any
// FormattedText renderer below the provider. We thread via context so
// deeply-nested components (replies, embeds, popovers) get the lookup
// without prop drilling. Defaults to empty when no server is in scope
// (DM channels and similar).
// =============================================================================
const ServerEmojiContext = createContext<ServerEmoji[]>([]);

// Shared style for the composer's flat utility icon-buttons (attach / GIF /
// emoji). Uniform 32px square, no per-button bevel — they sit *inside* the
// already-beveled composer box, so a ghost treatment (muted glyph that
// brightens + gets a subtle fill on hover) keeps the box clean instead of
// stacking raised pills. Send stays the one accent button for hierarchy.
const COMPOSER_ICON_BTN =
  'shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-aim ' +
  'text-text-subtle hover:text-text hover:bg-surface active:bg-panel ' +
  'transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';

interface Props {
  channel: Channel;
  serverId: string | null;
  /** Caller's role in this server (null for DMs). Gates the pin action. */
  myRole?: import('../permissions').Role | null;
}

// =============================================================================
// Reply context — when set, the next send becomes a reply
// =============================================================================
interface ReplyContext {
  messageId: string;
  authorName: string;
  preview: string;
}

interface PendingAttachment {
  /** Unique id for React key + remove operations. */
  id: string;
  /** MinIO storage key already uploaded. Used at send time. */
  storageKey: string;
  filename: string;
  contentType: string;
  /** Local object URL for thumbnail preview. Revoked when removed/sent. */
  previewUrl: string | null;
  sizeBytes: number;
}

export function TextChannelView({ channel, serverId, myRole }: Props) {
  const { messages, loading, hasMore, loadMore, error, toggleReaction } = useMessages(channel.id);
  const me = useApp((s) => s.user);
  const soundsEnabled = useApp((s) => s.soundsEnabled);
  // useMembers expects a server. For DMs there are no members in the
  // server-list sense — pass null and the hook returns an empty list.
  const members = useMembers(serverId);
  const typingUsers = useTypingUsers(channel.id);
  // Custom server emoji for this channel's server. Empty in DMs.
  const { emojis: serverEmojis } = useServerEmojis(serverId);

  const scrollerRef = useRef<HTMLDivElement>(null);
  // Wraps the rendered message list; observed so late height changes re-pin
  // the view to the bottom (see the ResizeObserver effect below).
  const contentRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState(() => loadDraft(channel.id));
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [draggingFile, setDraggingFile] = useState(false);
  const [editing, setEditing] = useState<{ id: string; original: string } | null>(null);
  const [replyTo, setReplyTo] = useState<ReplyContext | null>(null);
  // Hoisted here rather than per-MessageItem: messages are .map-rendered, so a
  // dialog per row would mount thousands of them.
  const [pendingDeleteMessage, setPendingDeleteMessage] = useState<Message | null>(null);
  const [mentionState, setMentionState] = useState<{
    open: boolean;
    query: string;
    position: number;  // cursor offset of the @ that started the mention
  }>({ open: false, query: '', position: -1 });
  const [emojiState, setEmojiState] = useState<{
    open: boolean;
    query: string;
    position: number;  // cursor offset of the ':' that started the shortcode
  }>({ open: false, query: '', position: -1 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stickToBottomRef = useRef(true);
  const lastTypingRef = useRef(0);
  // Pending attachments — uploaded to MinIO but NOT yet sent as a message.
  // Lets the user paste/drag/pick files, see thumbnails, optionally type
  // some accompanying text, and only send when they hit Enter (or click
  // send). Replaces the old behavior of paste→immediately send. Cleared
  // on send, on channel switch, or when the user removes them individually.
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  // Tracks the channel id whose messages we've already auto-scrolled into
  // view after first load. Used to fire a one-time "land at the bottom"
  // scroll on every channel switch — but only AFTER messages have rendered.
  // Without this, the channel-change effect tries to scroll before any
  // message DOM exists, so the scroller stays at the top.
  const lastLoadedChannelRef = useRef<string | null>(null);

  // --- In-channel message search (Discord-style: header search box with a
  // filter dropdown, results in a right-hand sidebar). Scoped to this
  // channel/DM only. ---
  type SearchFilters = { text?: string; from?: string; has?: 'link' | 'file' | 'image' };
  const [searchActive, setSearchActive] = useState(false);
  const [searchQ, setSearchQ] = useState<SearchFilters | null>(null);
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);

  async function runSearch(params: SearchFilters, offset = 0) {
    if (!params.text && !params.from && !params.has) return;
    setSearchActive(true);
    setSearchQ(params);
    setSearchLoading(true);
    setSearchErr(null);
    try {
      const res = await api.searchMessages(channel.id, { ...params, limit: 25, offset });
      setSearchResults((prev) => (offset > 0 ? [...prev, ...res.messages] : res.messages));
      setSearchHasMore(res.hasMore);
    } catch (e) {
      setSearchErr(e instanceof ApiError ? e.message : 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  }

  function closeSearch() {
    setSearchActive(false);
    setSearchQ(null);
    setSearchResults([]);
    setSearchHasMore(false);
    setSearchErr(null);
  }

  // Briefly flash + scroll a message into view (when it's loaded in the
  // scrollback). Clicking a search result that isn't in the loaded window is
  // a no-op for now (no "load around" endpoint yet).
  function jumpToMessage(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (el) {
      // Release stick-to-bottom first, or the ResizeObserver re-pin would yank
      // a search / `?m=` jump straight back down as images finish loading.
      stickToBottomRef.current = false;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('search-flash');
      window.setTimeout(() => el.classList.remove('search-flash'), 1600);
    }
  }

  // Build a shareable universal link to a specific message in this channel.
  // Mirrors how AppShell routes channels (`/s/<serverId>/<name>`, `/dm/<id>`)
  // with the message id carried as `?m=` so opening the link lands on the
  // message (see the jump-on-load effect below).
  function copyMessageLink(message: Message) {
    const base =
      channel.type === 'dm'
        ? `/dm/${channel.id}`
        : `/s/${channel.serverId ?? serverId}/${channel.name}`;
    const link = pathToShareLink(`${base}?m=${message.id}`);
    void navigator.clipboard.writeText(link).catch(() => {});
  }

  // Jump-on-load: when arrived via a message deep link (`?m=<id>`), scroll to
  // and flash that message once its scrollback has rendered. Best-effort and
  // bounded to messages in the loaded window — same limitation as search
  // result jumps (no "load around" endpoint yet).
  const [searchParams] = useSearchParams();
  const jumpParam = searchParams.get('m');
  const jumpedParamRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !jumpParam) return;
    if (jumpedParamRef.current === jumpParam) return;
    jumpedParamRef.current = jumpParam;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => jumpToMessage(jumpParam)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, jumpParam, messages.length]);

  // Subscribe/unsubscribe to channel events when entering/leaving
  useEffect(() => {
    wsClient.subscribeToChannel(channel.id);
    return () => wsClient.unsubscribeFromChannel(channel.id);
  }, [channel.id]);

  // Close any open search when switching channels.
  useEffect(() => {
    closeSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.id]);

  const clearUnread = useApp((s) => s.clearUnread);

  // Play a "ding" when a new message arrives from someone else, AND keep
  // the backend's last_read_at advancing while the user is watching this
  // channel. Without the latter, refreshing the page after sitting in a
  // channel and watching messages stream in would resurrect the unread
  // badge — the backend never knew the user had seen them. We throttle
  // the mark-read POST to once per 3s per channel-mount.
  const lastMessageIdRef = useRef<string | null>(null);
  const lastMarkReadAtRef = useRef(0);
  // Count of messages that have arrived while the user is scrolled up away
  // from the bottom. Drives the "↓ N new messages" pill above the composer.
  // Resets when we scroll back to the bottom.
  const [newMessagesBelow, setNewMessagesBelow] = useState(0);

  // Reset the new-message baseline when switching channels. This component is
  // reused across channels (no key), so without this the ref still holds the
  // PREVIOUS channel's last message id — and the next channel's backfilled
  // messages get mistaken for "new" arrivals, playing the message sound (and
  // bumping the new-messages pill) on every channel/DM switch. Declared before
  // the sound effect so it runs first on a channel change.
  useEffect(() => {
    lastMessageIdRef.current = null;
    setNewMessagesBelow(0);
    // Swap in this channel's saved draft (the component instance is reused
    // across channels, so state doesn't reset on its own). Any in-progress
    // edit/reply belongs to the channel we just left — clear them.
    setDraft(loadDraft(channel.id));
    setEditing(null);
    setReplyTo(null);
  }, [channel.id]);

  useEffect(() => {
    if (messages.length === 0) {
      lastMessageIdRef.current = null;
      return;
    }
    const newest = messages[messages.length - 1];
    if (!newest) return;
    // `messages` lags a render behind `channel.id` on a switch (useMessages
    // updates async). If these messages belong to a DIFFERENT channel, they're
    // stale — ignore them. Otherwise the stale newest consumes the first-load
    // guard below (advancing the ref to the OLD channel's last message), and
    // the next render dings for the newly-selected channel. This was the actual
    // channel-switch ding; resetting the ref on channel change wasn't enough
    // because this effect also runs on channel.id change, with stale messages.
    if (newest.channelId !== channel.id) return;
    if (lastMessageIdRef.current === null) {
      // First load — don't play sound for backfilled messages
      lastMessageIdRef.current = newest.id;
      return;
    }
    if (lastMessageIdRef.current !== newest.id && newest.author.id !== me?.id) {
      // A mention gets its own chime so you can tell "someone said something"
      // from "someone said something to ME" without looking at the screen.
      if (soundsEnabled) {
        playSound(isMention(newest.content, me?.username) ? 'mention' : 'message');
      }

      // If the user has scrolled up away from the bottom, surface the
      // arrival as a "new messages" pill rather than silently pushing it
      // off-screen.
      if (!stickToBottomRef.current) {
        setNewMessagesBelow((n) => n + 1);
      }

      // Mark as read on the server too — but only when the tab is focused
      // (no point pretending we read it if the user's looking elsewhere)
      // and at most once every 3 seconds.
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastMarkReadAtRef.current > 3000) {
          lastMarkReadAtRef.current = now;
          clearUnread(channel.id);
          api.markChannelRead(channel.id).catch(() => {});
        }
      }
    }
    lastMessageIdRef.current = newest.id;
  }, [messages, me?.id, soundsEnabled, channel.id, clearUnread]);

  // When the tab regains visibility, also mark this channel as read —
  // catches the case where messages arrived while we were on another tab
  // and the per-message effect skipped the POST due to !visible.
  useEffect(() => {
    function onVis() {
      if (document.visibilityState !== 'visible') return;
      lastMarkReadAtRef.current = Date.now();
      clearUnread(channel.id);
      api.markChannelRead(channel.id).catch(() => {});
    }
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [channel.id, clearUnread]);

  // ---------------- Scroll handling ----------------
  function onScroll() {
    const el = scrollerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < 100;
    stickToBottomRef.current = atBottom;
    // Hitting the bottom dismisses the new-messages pill.
    if (atBottom && newMessagesBelow > 0) setNewMessagesBelow(0);

    if (el.scrollTop < 80 && hasMore && !loading) {
      const prevHeight = el.scrollHeight;
      loadMore().then(() => {
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      });
    }
  }

  function jumpToBottom() {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    stickToBottomRef.current = true;
    setNewMessagesBelow(0);
  }

  useEffect(() => {
    if (stickToBottomRef.current && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Stay pinned while content grows without `messages.length` changing:
  // images/GIFs/avatars finishing loading, link embeds (`message.embed` lands
  // after the message), reactions, edits. The first-load effect below runs two
  // rAFs after render, which is before most of that settles — without this,
  // entering a media-heavy channel stops short of the bottom.
  useEffect(() => {
    const content = contentRef.current;
    const el = scrollerRef.current;
    if (!content || !el) return;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  // On channel switch: reset state. We DON'T scroll here — the messages
  // haven't rendered yet, so scrollHeight reflects the previous channel's
  // content. Actual scroll happens in the next effect once `loading` flips
  // to false, meaning useMessages has finished fetching.
  useEffect(() => {
    stickToBottomRef.current = true;
    setNewMessagesBelow(0);
    // Force the next loading=false to trigger an auto-scroll, even if
    // we've seen this channel id before.
    lastLoadedChannelRef.current = null;
  }, [channel.id]);

  // First-load auto-scroll. Fires when messages for the current channel
  // have actually loaded (loading transitions false). We only do this once
  // per channel entry — afterwards normal stick-to-bottom behavior takes
  // over via the messages.length effect above.
  useEffect(() => {
    if (loading) return;
    if (lastLoadedChannelRef.current === channel.id) return;
    lastLoadedChannelRef.current = channel.id;
    // Two rAFs: one for React to commit the message DOM, one for layout
    // to settle (image placeholders, embeds, etc, can shift heights).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
        stickToBottomRef.current = true;
        setNewMessagesBelow(0);
      });
    });
  }, [channel.id, loading]);

  // ---------------- Send / Edit / Delete ----------------
  async function send() {
    const text = draft.trim();
    const hasPending = pendingAttachments.length > 0;
    // Allow sending with text OR attachments OR both. Empty + no attachments
    // is a no-op.
    if (!text && !hasPending) return;
    if (sending) return;
    setSending(true);
    // Snapshot pending so we can restore on error.
    const snapshot = pendingAttachments;
    setDraft('');
    clearDraft(channel.id);
    setPendingAttachments([]);
    try {
      if (hasPending) {
        // Finalize uploaded attachments + optional accompanying text.
        await api.finalizeUpload({
          channelId: channel.id,
          uploads: snapshot.map((p) => ({
            storageKey: p.storageKey,
            filename: p.filename,
            contentType: p.contentType,
          })),
          content: text || undefined,
          replyToId: replyTo?.messageId,
        });
        // Revoke preview URLs now that the attachments are sent.
        for (const p of snapshot) {
          if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        }
      } else {
        await api.sendMessage(channel.id, text, replyTo?.messageId);
      }
      setReplyTo(null);
      stickToBottomRef.current = true;
    } catch (err) {
      console.error('send failed', err);
      setDraft(text);
      saveDraft(channel.id, text);
      // Restore pending so the user doesn't lose their uploads on error.
      setPendingAttachments(snapshot);
      toast.error(err, 'Failed to send');
    } finally {
      setSending(false);
    }
  }

  async function commitEdit() {
    if (!editing) return;
    const text = draft.trim();
    if (!text) return;
    if (text === editing.original) {
      cancelEdit();
      return;
    }
    setSending(true);
    try {
      await api.editMessage(editing.id, text);
      // Restore the channel's pending draft (if any) rather than blanking —
      // editing a message shouldn't discard what you were composing.
      setDraft(loadDraft(channel.id));
      setEditing(null);
    } catch (err) {
      toast.error(err, 'Edit failed');
    } finally {
      setSending(false);
    }
  }

  function cancelEdit() {
    setEditing(null);
    setDraft(loadDraft(channel.id));
  }

  function startEdit(message: Message) {
    if (replyTo) setReplyTo(null);
    setEditing({ id: message.id, original: message.content });
    setDraft(message.content);
    requestAnimationFrame(() => composerRef.current?.focus());
  }

  function startReply(message: Message) {
    if (editing) cancelEdit();
    const preview = message.content.length > 80 ? message.content.slice(0, 80) + '…' : message.content;
    setReplyTo({
      messageId: message.id,
      authorName: message.author.displayName || message.author.username,
      preview: preview || (message.attachments?.length ? `[${message.attachments.length} attachment(s)]` : ''),
    });
    requestAnimationFrame(() => composerRef.current?.focus());
  }

  async function deleteMessage(message: Message) {
    setPendingDeleteMessage(message);
  }

  async function confirmDeleteMessage() {
    const message = pendingDeleteMessage;
    setPendingDeleteMessage(null);
    if (!message) return;
    try {
      await api.deleteMessage(message.id);
    } catch (err) {
      toast.error(err, 'Delete failed');
    }
  }

  // Can the current user pin/unpin in this channel? Either DM participant, or
  // a server member with moderate_messages (owner/admin/moderator). The server
  // is the source of truth — this only gates the UI.
  const canPin = channel.type === 'dm' || hasPermission(myRole ?? null, 'moderate_messages');

  async function togglePin(message: Message) {
    try {
      if (message.pinned) await api.unpinMessage(message.id);
      else await api.pinMessage(message.id);
      // The live update arrives via the message.pinned/unpinned WS event.
    } catch (err) {
      toast.error(err, `${message.pinned ? 'Unpin' : 'Pin'} failed`);
    }
  }

  // ---------------- Typing throttle ----------------
  function maybeBroadcastTyping() {
    if (editing) return;  // don't broadcast typing while editing
    const now = Date.now();
    if (now - lastTypingRef.current < 3000) return;  // throttle to once per 3s
    lastTypingRef.current = now;
    wsClient.send({ type: 'typing.start', channelId: channel.id });
  }

  // ---------------- Mention autocomplete ----------------
  // Reset mention state on channel switch
  useEffect(() => {
    setMentionState({ open: false, query: '', position: -1 });
  }, [channel.id]);

  // Filter members for autocomplete
  const mentionCandidates: Member[] = useMemo(() => {
    if (!mentionState.open) return [];
    const q = mentionState.query.toLowerCase();
    return members
      .filter((m) => m.userId !== me?.id)
      .filter((m) => {
        const u = m.username.toLowerCase();
        const d = (m.displayName || '').toLowerCase();
        return u.startsWith(q) || d.startsWith(q) || u.includes(q) || d.includes(q);
      })
      .slice(0, 8);
  }, [mentionState.open, mentionState.query, members, me?.id]);

  const [mentionIndex, setMentionIndex] = useState(0);
  useEffect(() => { setMentionIndex(0); }, [mentionState.query, mentionState.open]);

  function detectMention(text: string, caret: number) {
    // Walk backward from caret looking for an @ unbroken by whitespace
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === '@') {
        // Need preceding char to be whitespace or start of string
        const before = i > 0 ? text[i - 1] ?? '' : '';
        if (before === '' || /\s/.test(before)) {
          const query = text.slice(i + 1, caret);
          if (/^[a-zA-Z0-9_.\-]*$/.test(query) && query.length <= 32) {
            return { position: i, query };
          }
        }
        return null;
      }
      if (!ch || /\s/.test(ch)) return null;
      i--;
    }
    return null;
  }

  // ---------------- Emoji shortcode autocomplete (`:smile`) ----------------
  // Mirrors the mention machinery above. The shortcuts cheatsheet has always
  // advertised ':' as a composer shortcut; this is the implementation.
  function detectEmojiShortcode(text: string, caret: number) {
    // Walk backward from caret looking for a ':' unbroken by whitespace.
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === ':') {
        const before = i > 0 ? text[i - 1] ?? '' : '';
        if (before === '' || /\s/.test(before)) {
          const query = text.slice(i + 1, caret);
          // Require 2+ chars: firing on a bare ':' would pop the menu open
          // constantly (timestamps, "note:", emoticons like ":)").
          if (/^[a-zA-Z0-9_+-]{2,32}$/.test(query)) {
            return { position: i, query };
          }
        }
        return null;
      }
      if (!ch || /\s/.test(ch)) return null;
      i--;
    }
    return null;
  }

  const emojiCandidates: EmojiCandidate[] = useMemo(() => {
    if (!emojiState.open) return [];
    const q = emojiState.query.toLowerCase();
    const out: EmojiCandidate[] = [];
    // Custom server emoji first — they're this community's own, and there are
    // few enough that they'd otherwise get buried under the unicode set.
    for (const e of serverEmojis) {
      if (e.name.toLowerCase().includes(q)) {
        out.push({ kind: 'custom', name: e.name, url: e.url });
      }
    }
    for (const cat of EMOJI_CATEGORIES) {
      for (const e of cat.emoji) {
        // `keywords` is space-separated; match a keyword prefix so ":gr" finds
        // "grinning" but ":ing" doesn't.
        if (e.keywords.split(' ').some((k) => k.startsWith(q))) {
          out.push({ kind: 'unicode', name: e.keywords.split(' ')[0] ?? q, glyph: e.glyph });
        }
      }
    }
    return out.slice(0, 8);
  }, [emojiState.open, emojiState.query, serverEmojis]);

  const [emojiIndex, setEmojiIndex] = useState(0);
  useEffect(() => { setEmojiIndex(0); }, [emojiState.query, emojiState.open]);

  useEffect(() => {
    setEmojiState({ open: false, query: '', position: -1 });
  }, [channel.id]);

  function applyEmoji(cand: EmojiCandidate) {
    if (emojiState.position < 0) return;
    const before = draft.slice(0, emojiState.position);
    const after = draft.slice(emojiState.position + 1 + emojiState.query.length);
    // Custom emoji stay as `:name:` shortcodes — that's what the tokenizer
    // resolves against. Unicode emoji are inserted as the glyph itself.
    const insert = cand.kind === 'custom' ? `:${cand.name}: ` : `${cand.glyph} `;
    const newDraft = `${before}${insert}${after}`;
    setDraft(newDraft);
    if (!editing) saveDraft(channel.id, newDraft);
    setEmojiState({ open: false, query: '', position: -1 });
    requestAnimationFrame(() => {
      const pos = before.length + insert.length;
      if (composerRef.current) {
        composerRef.current.setSelectionRange(pos, pos);
        composerRef.current.focus();
      }
    });
  }

  function onComposerInput(e: ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setDraft(value);
    // Persist as the channel draft (but not while editing an existing message —
    // that content isn't a draft for this channel).
    if (!editing) saveDraft(channel.id, value);
    maybeBroadcastTyping();

    const caret = e.target.selectionStart ?? value.length;
    const detected = detectMention(value, caret);
    if (detected) {
      setMentionState({ open: true, query: detected.query, position: detected.position });
    } else if (mentionState.open) {
      setMentionState({ open: false, query: '', position: -1 });
    }

    // Only one popover at a time — a mention wins if both somehow match.
    const emojiDetected = detected ? null : detectEmojiShortcode(value, caret);
    if (emojiDetected) {
      setEmojiState({ open: true, query: emojiDetected.query, position: emojiDetected.position });
    } else if (emojiState.open) {
      setEmojiState({ open: false, query: '', position: -1 });
    }
  }

  function applyMention(member: Member) {
    if (mentionState.position < 0) return;
    const before = draft.slice(0, mentionState.position);
    const after = draft.slice(mentionState.position + 1 + mentionState.query.length);
    const newDraft = `${before}@${member.username} ${after}`;
    setDraft(newDraft);
    saveDraft(channel.id, newDraft);
    setMentionState({ open: false, query: '', position: -1 });
    requestAnimationFrame(() => {
      const pos = before.length + member.username.length + 2;  // +1 for @ +1 for space
      if (composerRef.current) {
        composerRef.current.setSelectionRange(pos, pos);
        composerRef.current.focus();
      }
    });
  }

  function onComposerKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Mention autocomplete navigation
    if (mentionState.open && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const member = mentionCandidates[mentionIndex];
        if (member) {
          e.preventDefault();
          applyMention(member);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState({ open: false, query: '', position: -1 });
        return;
      }
    }

    // Emoji shortcode autocomplete navigation (same key contract as mentions)
    if (emojiState.open && emojiCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setEmojiIndex((i) => (i + 1) % emojiCandidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setEmojiIndex((i) => (i - 1 + emojiCandidates.length) % emojiCandidates.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const cand = emojiCandidates[emojiIndex];
        if (cand) {
          e.preventDefault();
          applyEmoji(cand);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setEmojiState({ open: false, query: '', position: -1 });
        return;
      }
    }

    if (e.key === 'Escape') {
      if (editing) {
        e.preventDefault();
        cancelEdit();
        return;
      }
      if (replyTo) {
        e.preventDefault();
        setReplyTo(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editing) commitEdit();
      else send();
    }
  }

  // ---------------- File uploads ----------------
  // Upload files to MinIO and add them to the pending queue. Does NOT send
  // a message — that happens when the user hits Enter / clicks send. The
  // user can also remove individual pending attachments before sending.
  // This replaces an older behavior where pasting an image would
  // immediately send a message; that surprised people typing accompanying
  // text. Now the user is always in control of the send.
  async function uploadFiles(files: FileList | File[]) {
    const fileArray = Array.from(files).slice(0, 10);
    if (fileArray.length === 0) return;
    for (const file of fileArray) {
      try {
        setUploading(file.name);
        const presign = await api.presignUpload({
          channelId: channel.id,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        });
        await api.uploadDirect(presign.uploadUrl, file);
        // Build a local preview URL for image types so the thumbnail
        // appears in the composer. Revoked on remove or send.
        const previewUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null;
        const next: PendingAttachment = {
          id: crypto.randomUUID(),
          storageKey: presign.storageKey,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          previewUrl,
          sizeBytes: file.size,
        };
        setPendingAttachments((prev) => [...prev, next]);
      } catch (err) {
        console.error('upload failed', err);
        toast.error(err, 'Upload failed');
      } finally {
        setUploading(null);
      }
    }
    // After uploads queue up, focus the composer so the user can type and hit
    // send without an extra click.
    requestAnimationFrame(() => composerRef.current?.focus());
  }

  function removePendingAttachment(id: string) {
    setPendingAttachments((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      // We don't bother trying to delete the MinIO object — the upload TTL
      // (or our admin storage cleanup) will reap unused uploads. Removing
      // requires no network call so the UX is instant.
      return prev.filter((p) => p.id !== id);
    });
  }

  // Clear pending attachments on channel switch — they belong to the
  // channel they were uploaded to. Also revoke any preview URLs to free
  // memory.
  useEffect(() => {
    return () => {
      setPendingAttachments((prev) => {
        for (const p of prev) {
          if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        }
        return [];
      });
    };
  }, [channel.id]);

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDraggingFile(false);
    if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
  }

  function onFileSelect(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) uploadFiles(e.target.files);
    e.target.value = '';
  }

  // ---------------- Render ----------------
  const replyMap = useMemo(() => {
    const map = new Map<string, Message>();
    for (const m of messages) map.set(m.id, m);
    return map;
  }, [messages]);

  return (
    <ServerEmojiContext.Provider value={serverEmojis}>
    <div
      className="flex flex-col h-full min-h-0 relative"
      onDragEnter={(e) => { e.preventDefault(); setDraggingFile(true); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDragLeave={(e) => {
        e.preventDefault();
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDraggingFile(false);
      }}
      onDrop={onDrop}
    >
      {draggingFile && (
        <div className="absolute inset-0 z-30 bg-accent/20 backdrop-blur-xs flex items-center justify-center pointer-events-none">
          <div className="aim-window p-8">
            <div className="text-2xl font-bold flex items-center gap-3">
              <Icon name="paperclip" size={28} />
              Drop to upload
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-surface shrink-0">
        <span className="font-bold text-text-muted text-lg leading-none">#</span>
        <h2 className="font-bold text-text whitespace-nowrap">{channel.name}</h2>
        {channel.topic && (
          <>
            <span className="text-text-subtle px-1">|</span>
            <span className="text-xs text-text-muted truncate">{channel.topic}</span>
          </>
        )}
        <div className="ml-auto flex items-center gap-1 shrink-0 pl-2">
          {channel.type === 'dm' && (
            <button
              type="button"
              onClick={() => startCall(channel.id, channel.name)}
              title={`Call ${channel.name}`}
              className="h-8 w-8 inline-flex items-center justify-center rounded-aim text-text-subtle hover:text-text hover:bg-elevated transition-colors"
            >
              <Icon name="phone" size={16} />
            </button>
          )}
          <PinsButton channelId={channel.id} canPin={canPin} onJump={jumpToMessage} />
          <ChannelSearch
            members={members}
            active={searchActive}
            onSearch={(p) => runSearch(p)}
            onClear={closeSearch}
          />
        </div>
      </div>

      {/* Horizontal split: message column + optional search results sidebar. */}
      {/* min-w-0 on both: a flex child otherwise can't shrink below its widest
          unbreakable content — a code block's `pre` line — so on a phone the
          whole column, composer and Send included, ran ~70px off-screen. */}
      <div className="flex flex-1 min-h-0 min-w-0">
      <div className="flex flex-col flex-1 min-h-0 min-w-0">
      <div ref={scrollerRef} onScroll={onScroll} className="flex-1 overflow-y-auto min-h-0 p-2">
        {loading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-text-muted gap-2">
            <Spinner /> Loading messages...
          </div>
        )}
        {error && <div className="text-danger p-4">{error}</div>}
        {!loading && messages.length === 0 && !error && <EmptyChannel channel={channel} />}
        {hasMore && (
          <div className="text-center py-2 text-xs text-text-subtle">
            {loading ? 'Loading older messages…' : 'Scroll up for more'}
          </div>
        )}
        <div ref={contentRef}>
        <MessageList
          messages={messages}
          myUserId={me?.id ?? null}
          replyMap={replyMap}
          canPin={canPin}
          onEdit={startEdit}
          onDelete={deleteMessage}
          onReply={startReply}
          onTogglePin={togglePin}
          onCopyLink={copyMessageLink}
          onToggleReaction={toggleReaction}
        />
        </div>
      </div>

      {/* Typing indicator */}
      <TypingIndicator users={typingUsers.filter((u) => u.userId !== me?.id)} />

      {/* New-messages-below pill — shown when the user is scrolled up and
          a message has arrived. Click to jump to the bottom. */}
      {newMessagesBelow > 0 && (
        <div className="px-3 -mb-1 flex justify-center pointer-events-none relative">
          <button
            type="button"
            onClick={jumpToBottom}
            className="pointer-events-auto absolute -top-9 bg-accent text-accent-text text-xs font-semibold px-3 py-1 rounded-full shadow-aim-window hover:opacity-90 transition-opacity flex items-center gap-1.5"
          >
            <span>↓</span>
            <span>
              {newMessagesBelow} new {newMessagesBelow === 1 ? 'message' : 'messages'}
            </span>
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-1 border-t border-border bg-surface shrink-0">
        {/* Reply preview banner */}
        {replyTo && (
          <div className="rounded-xl border border-border-soft bg-elevated px-3 py-1.5 mb-1.5 flex items-center gap-2">
            <Icon name="back" size={12} className="text-text-subtle" />
            <span className="text-xs text-text-muted">
              Replying to <strong className="text-link">{replyTo.authorName}</strong>
              {replyTo.preview && <span className="text-text-subtle">: {replyTo.preview}</span>}
            </span>
            <button
              onClick={() => setReplyTo(null)}
              className="ml-auto text-text-subtle hover:text-text text-base leading-none"
              title="Cancel reply"
            >
              ×
            </button>
          </div>
        )}

        {/* Edit banner */}
        {editing && (
          <div className="rounded-xl border border-accent/40 bg-accent/15 px-3 py-1.5 mb-1.5 flex items-center gap-2">
            <span className="text-xs text-text">
              Editing message — Esc to cancel, Enter to save
            </span>
            <button
              onClick={cancelEdit}
              className="ml-auto text-text-subtle hover:text-text text-base leading-none"
            >
              ×
            </button>
          </div>
        )}

        {uploading && (
          <div className="text-xs text-text-muted mb-1.5 flex items-center gap-2">
            <Spinner size={12} /> Uploading {uploading}…
          </div>
        )}

        {pendingAttachments.length > 0 && (
          <PendingAttachmentsStrip
            attachments={pendingAttachments}
            onRemove={removePendingAttachment}
          />
        )}

        <div className="hybrid-composer flex items-start gap-1 p-1.5 relative">
          {/* Utility cluster — attach / GIF / emoji, grouped on the left as
              one uniform set of flat icon-buttons. */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Attach file"
            className={COMPOSER_ICON_BTN}
            disabled={!!uploading || !!editing}
          >
            <Icon name="paperclip" size={18} />
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFileSelect} />
          <ComposerGifButton
            onAttach={async ({ fullUrl }) => {
              // Stage the GIF on the server (downloads from Giphy + puts
              // into MinIO). Returns the storage key + metadata in the
              // same shape as a regular pasted/dropped upload, so we can
              // push directly into pendingAttachments and let the existing
              // send flow handle the rest.
              try {
                const staged = await api.gifsStage({ fullUrl });
                const next: PendingAttachment = {
                  id: crypto.randomUUID(),
                  storageKey: staged.storageKey,
                  filename: staged.filename,
                  contentType: staged.contentType,
                  // Use the Giphy preview URL directly — it's already
                  // hosted, no extra round-trip needed for the thumbnail.
                  previewUrl: staged.previewUrl,
                  sizeBytes: staged.sizeBytes,
                };
                setPendingAttachments((prev) => [...prev, next]);
                // Focus composer so the user can immediately type a caption
                // and hit Enter to send.
                requestAnimationFrame(() => composerRef.current?.focus());
              } catch (err) {
                toast.error(err, 'GIF attach failed');
                throw err;  // re-throw so the picker keeps the spinner state correct
              }
            }}
            disabled={!!editing || sending}
          />
          <ComposerEmojiButton
            onPick={(emoji) => {
              // Insert at the textarea's current cursor position so picks
              // mid-message land where the user was typing rather than at
              // the very end. After insertion we restore focus + put the
              // cursor right after the inserted glyph.
              const ta = composerRef.current;
              const start = ta?.selectionStart ?? draft.length;
              const end = ta?.selectionEnd ?? draft.length;
              const next = draft.slice(0, start) + emoji + draft.slice(end);
              setDraft(next);
              if (!editing) saveDraft(channel.id, next);
              requestAnimationFrame(() => {
                if (composerRef.current) {
                  composerRef.current.focus();
                  const pos = start + emoji.length;
                  composerRef.current.setSelectionRange(pos, pos);
                }
              });
            }}
            disabled={!!editing || sending}
          />
          {/* Subtle divider separating the utility cluster from the input.
              h-8 + self-start so it spans exactly the icon row and tops out
              level with them (the box can grow taller as you type). */}
          <div aria-hidden className="w-px h-8 self-start bg-border-soft shrink-0 mx-1" />
          <textarea
            ref={composerRef}
            value={draft}
            onChange={onComposerInput}
            onKeyDown={onComposerKeyDown}
            onPaste={(e) => {
              // If the clipboard contains files (most commonly: a screenshot
              // pasted from the OS clipboard, or a copy-image action from a
              // browser), upload them. Default text paste is left alone.
              if (e.clipboardData.files.length > 0) {
                e.preventDefault();
                uploadFiles(e.clipboardData.files);
              }
            }}
            placeholder={editing ? 'Edit your message…' : `Message #${channel.name}…`}
            rows={2}
            className="flex-1 bg-transparent border-0 outline-hidden resize-none text-sm py-2 px-2 max-h-32 min-h-[52px] font-sans text-text"
            // Auto-grow with content. Floor matches min-h above so the box
            // stays at its prominent size when empty; ceiling caps at the
            // max-h so very long drafts get a scrollbar instead of pushing
            // the chat scroller around.
            style={{ height: Math.min(128, Math.max(52, draft.split('\n').length * 22 + 22)) }}
            disabled={sending}
          />
          <Button
            variant="primary"
            onClick={editing ? commitEdit : send}
            disabled={(!draft.trim() && pendingAttachments.length === 0) || sending}
            className="px-3! py-2! shrink-0 h-8"
            title={editing ? 'Save (Enter)' : 'Send (Enter)'}
          >
            {sending ? <Spinner size={14} /> : <Icon name={editing ? 'check' : 'send'} size={16} />}
          </Button>

          {/* Mention popover */}
          {mentionState.open && mentionCandidates.length > 0 && (
            <MentionPopover
              candidates={mentionCandidates}
              activeIndex={mentionIndex}
              onPick={applyMention}
              onHover={setMentionIndex}
            />
          )}

          {/* Emoji shortcode popover */}
          {emojiState.open && emojiCandidates.length > 0 && (
            <EmojiShortcodePopover
              candidates={emojiCandidates}
              activeIndex={emojiIndex}
              onPick={applyEmoji}
              onHover={setEmojiIndex}
            />
          )}
        </div>
        <div className="text-[10px] text-text-subtle mt-1 px-1">
          {editing
            ? 'Esc to cancel · Enter to save'
            : 'Enter to send · Shift+Enter for new line · @ to mention · : for emoji · drag files anywhere'}
        </div>
      </div>
      </div>{/* end message column */}

      {searchActive && searchQ && (
        <SearchResultsSidebar
          query={searchQ}
          results={searchResults}
          loading={searchLoading}
          hasMore={searchHasMore}
          error={searchErr}
          onJump={jumpToMessage}
          onClose={closeSearch}
          onLoadMore={() => runSearch(searchQ, searchResults.length)}
        />
      )}
      </div>{/* end horizontal split */}

      <ConfirmDialog
        open={!!pendingDeleteMessage}
        title="Delete message"
        body="Delete this message? This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => void confirmDeleteMessage()}
        onCancel={() => setPendingDeleteMessage(null)}
      />
    </div>
    </ServerEmojiContext.Provider>
  );
}

// =============================================================================
// MentionPopover — shown above the composer when typing @
// =============================================================================
function MentionPopover({
  candidates,
  activeIndex,
  onPick,
  onHover,
}: {
  candidates: Member[];
  activeIndex: number;
  onPick: (m: Member) => void;
  onHover: (i: number) => void;
}) {
  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 hybrid-card max-h-64 overflow-y-auto z-20 animate-slide-up">
      <div className="hybrid-card-header py-1! text-[11px]!">Mention someone</div>
      <div className="bg-surface">
        {candidates.map((m, i) => {
          const name = m.displayName || m.username;
          return (
            <div
              key={m.userId}
              onMouseEnter={() => onHover(i)}
              onClick={() => onPick(m)}
              className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer ${
                i === activeIndex ? 'bg-accent text-accent-text' : 'hover:bg-elevated'
              }`}
            >
              <Avatar name={name} url={m.avatarUrl} size={20} />
              <span className="text-sm font-medium">{name}</span>
              {m.displayName && (
                <span className={`text-xs ${i === activeIndex ? 'opacity-70' : 'text-text-subtle'}`}>
                  @{m.username}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// EmojiShortcodePopover — shown above the composer when typing `:name`
// =============================================================================
// Deliberately the same shell/keyboard contract as MentionPopover so the two
// autocompletes feel identical.
type EmojiCandidate =
  | { kind: 'custom'; name: string; url: string }
  | { kind: 'unicode'; name: string; glyph: string };

function EmojiShortcodePopover({
  candidates,
  activeIndex,
  onPick,
  onHover,
}: {
  candidates: EmojiCandidate[];
  activeIndex: number;
  onPick: (c: EmojiCandidate) => void;
  onHover: (i: number) => void;
}) {
  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 hybrid-card max-h-64 overflow-y-auto z-20 animate-slide-up">
      <div className="hybrid-card-header py-1! text-[11px]!">Emoji</div>
      <div className="bg-surface">
        {candidates.map((c, i) => (
          <div
            key={`${c.kind}:${c.name}`}
            onMouseEnter={() => onHover(i)}
            onClick={() => onPick(c)}
            className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer ${
              i === activeIndex ? 'bg-accent text-accent-text' : 'hover:bg-elevated'
            }`}
          >
            {c.kind === 'custom' ? (
              <img src={c.url} alt={c.name} className="w-5 h-5 object-contain" />
            ) : (
              <span className="w-5 h-5 flex items-center justify-center text-base leading-none">
                {c.glyph}
              </span>
            )}
            <span className="text-sm font-medium">:{c.name}:</span>
            {c.kind === 'custom' && (
              <span className={`text-xs ${i === activeIndex ? 'opacity-70' : 'text-text-subtle'}`}>
                server
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// TypingIndicator — floats above the composer
// =============================================================================
function TypingIndicator({ users }: { users: Array<{ userId: string; username: string }> }) {
  if (users.length === 0) return null;
  let text: string;
  if (users.length === 1) text = `${users[0]!.username} is typing…`;
  else if (users.length === 2) text = `${users[0]!.username} and ${users[1]!.username} are typing…`;
  else if (users.length === 3) text = `${users[0]!.username}, ${users[1]!.username}, and ${users[2]!.username} are typing…`;
  else text = 'Several people are typing…';

  return (
    <div className="px-4 py-1 text-xs text-text-muted italic flex items-center gap-2 bg-surface shrink-0 h-6">
      <span className="flex gap-0.5">
        <span className="inline-block w-1 h-1 rounded-full bg-text-muted animate-pulse" style={{ animationDelay: '0ms' }} />
        <span className="inline-block w-1 h-1 rounded-full bg-text-muted animate-pulse" style={{ animationDelay: '150ms' }} />
        <span className="inline-block w-1 h-1 rounded-full bg-text-muted animate-pulse" style={{ animationDelay: '300ms' }} />
      </span>
      {text}
    </div>
  );
}

// =============================================================================
// EmptyChannel
// =============================================================================
function EmptyChannel({ channel }: { channel: Channel }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center text-text-muted p-8">
      <div className="w-16 h-16 rounded-2xl bg-accent/15 ring-1 ring-accent/25 flex items-center justify-center text-3xl font-bold text-accent-text mb-3 shadow-[0_4px_12px_rgba(0,0,0,0.06)]">
        #
      </div>
      <div className="text-lg font-bold text-text">Welcome to #{channel.name}</div>
      <div className="text-sm mt-1">Be the first to say something! 👋</div>
    </div>
  );
}

// =============================================================================
// MessageList — handles grouping and date separators
// =============================================================================
function MessageList({
  messages,
  myUserId,
  replyMap,
  canPin,
  onEdit,
  onDelete,
  onReply,
  onTogglePin,
  onCopyLink,
  onToggleReaction,
}: {
  messages: Message[];
  myUserId: string | null;
  replyMap: Map<string, Message>;
  canPin: boolean;
  onEdit: (m: Message) => void;
  onDelete: (m: Message) => void;
  onReply: (m: Message) => void;
  onTogglePin: (m: Message) => void;
  onCopyLink: (m: Message) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
}) {
  return (
    <div className="space-y-0.5">
      {messages.map((m, idx) => {
        const prev = messages[idx - 1];
        const isContinuation =
          prev &&
          prev.author.id === m.author.id &&
          !prev.deleted &&
          !m.replyToId &&  // replies always start a fresh group
          new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000;

        const showDateSeparator =
          !prev || !sameDay(new Date(prev.createdAt), new Date(m.createdAt));

        // Collapse a run of same-day deletions into one line instead of a
        // stack of identical tombstones. Later rows keep their anchor id
        // (jump-to) but render nothing.
        if (m.deleted) {
          if (prev?.deleted && !showDateSeparator) return <div key={m.id} id={`msg-${m.id}`} />;
          let run = 1;
          while (
            messages[idx + run]?.deleted &&
            sameDay(new Date(messages[idx + run]!.createdAt), new Date(m.createdAt))
          ) run++;
          return (
            <div key={m.id} id={`msg-${m.id}`}>
              {showDateSeparator && <DateSeparator date={new Date(m.createdAt)} />}
              <div className="px-3 py-1 text-xs text-text-subtle italic">
                — {run === 1 ? 'this message was deleted' : `${run} messages were deleted`} —
              </div>
            </div>
          );
        }

        return (
          <div key={m.id} id={`msg-${m.id}`}>
            {showDateSeparator && <DateSeparator date={new Date(m.createdAt)} />}
            <MessageItem
              message={m}
              isContinuation={!!isContinuation}
              isMine={m.author.id === myUserId}
              repliedTo={m.replyToId ? replyMap.get(m.replyToId) ?? null : null}
              canPin={canPin}
              onEdit={onEdit}
              onDelete={onDelete}
              onReply={onReply}
              onTogglePin={onTogglePin}
              onCopyLink={onCopyLink}
              onToggleReaction={onToggleReaction}
            />
          </div>
        );
      })}
    </div>
  );
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function DateSeparator({ date }: { date: Date }) {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400_000);
  let label: string;
  if (sameDay(date, today)) label = 'Today';
  else if (sameDay(date, yesterday)) label = 'Yesterday';
  else label = date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="flex items-center gap-3 my-3 px-2">
      <div className="flex-1 h-px bg-border-soft" />
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold">{label}</div>
      <div className="flex-1 h-px bg-border-soft" />
    </div>
  );
}

// =============================================================================
// MessageItem — with hover actions
// =============================================================================
function MessageItem({
  message,
  isContinuation,
  isMine,
  repliedTo,
  canPin,
  onEdit,
  onDelete,
  onReply,
  onTogglePin,
  onCopyLink,
  onToggleReaction,
}: {
  message: Message;
  isContinuation: boolean;
  isMine: boolean;
  repliedTo: Message | null;
  canPin: boolean;
  onEdit: (m: Message) => void;
  onDelete: (m: Message) => void;
  onReply: (m: Message) => void;
  onTogglePin: (m: Message) => void;
  onCopyLink: (m: Message) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
}) {
  const time = new Date(message.createdAt);
  const timeStr = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (message.deleted) {
    return (
      <div className="px-3 py-1 text-xs text-text-subtle italic">
        <em>— this message was deleted —</em>
      </div>
    );
  }

  // System messages (call summaries, etc.) render as a centered, avatar-less
  // status line — not attributed to the author who happens to own the row.
  if (message.kind === 'system') {
    const missed = (message.meta as { reason?: string } | null)?.reason;
    const isMissed = missed === 'missed' || missed === 'declined' || missed === 'cancelled';
    return (
      <div className="flex items-center justify-center gap-2 my-1.5 px-3 text-xs">
        <Icon
          name={isMissed ? 'phone-off' : 'phone'}
          size={12}
          className={isMissed ? 'text-danger' : 'text-text-subtle'}
        />
        <span className={isMissed ? 'text-danger font-medium' : 'text-text-muted'}>
          {message.content}
        </span>
        <span className="text-[10px] text-text-subtle">{timeStr}</span>
      </div>
    );
  }

  const displayName = message.author.displayName || message.author.username;
  const reactions = message.reactions ?? [];

  // Right-click context menu — same actions as the hover bar, but reachable
  // anywhere on the row (and far more discoverable, esp. on continuation rows).
  const cm = useContextMenu();
  const menuItems: ContextMenuItem[] = [
    { label: 'Reply', icon: 'back', onClick: () => onReply(message) },
    ...(message.content
      ? [
          {
            label: 'Copy Text',
            icon: 'copy',
            onClick: () => void navigator.clipboard.writeText(message.content).catch(() => {}),
          } as ContextMenuItem,
        ]
      : []),
    { label: 'Copy Link', icon: 'copy', onClick: () => onCopyLink(message) },
    ...(canPin
      ? [
          { type: 'separator' } as ContextMenuItem,
          {
            label: message.pinned ? 'Unpin Message' : 'Pin Message',
            icon: message.pinned ? 'pin-off' : 'pin',
            onClick: () => onTogglePin(message),
          } as ContextMenuItem,
        ]
      : []),
    ...(isMine
      ? [
          ...(canPin ? [] : [{ type: 'separator' } as ContextMenuItem]),
          { label: 'Edit Message', icon: 'edit', onClick: () => onEdit(message) } as ContextMenuItem,
          { label: 'Delete Message', icon: 'trash', danger: true, onClick: () => onDelete(message) } as ContextMenuItem,
        ]
      : []),
  ];
  const contextMenu = cm.pos && (
    <ContextMenu x={cm.pos.x} y={cm.pos.y} items={menuItems} onClose={cm.close} />
  );

  // Reply context (small bar above the message)
  const replyHeader = repliedTo && !repliedTo.deleted && (
    <div className="flex items-center gap-1.5 text-xs text-text-muted ml-12 mb-0.5 pl-2 border-l-2 border-border-soft">
      <Icon name="back" size={10} className="opacity-50" />
      <span className="font-semibold text-link whitespace-nowrap shrink-0">{repliedTo.author.displayName || repliedTo.author.username}</span>
      <span className="truncate opacity-80">{repliedTo.content || '[attachment]'}</span>
    </div>
  );

  if (isContinuation) {
    return (
      <div className="group relative">
        {replyHeader}
        {contextMenu}
        <div className="flex gap-3 px-3 py-0.5 hover:bg-elevated/40 rounded-sm" onContextMenu={cm.open}>
          <div className="w-8 shrink-0 text-[10px] text-text-subtle text-right opacity-0 group-hover:opacity-100 pt-1 font-mono">
            {timeStr}
          </div>
          <div className="flex-1 min-w-0">
            <MessageContent message={message} />
            {reactions.length > 0 && (
              <MessageReactions
                messageId={message.id}
                reactions={reactions}
                onToggle={(emoji) => onToggleReaction(message.id, emoji)}
              />
            )}
          </div>
          <HoverActions
            message={message}
            isMine={isMine}
            canPin={canPin}
            onEdit={onEdit}
            onDelete={onDelete}
            onReply={onReply}
            onTogglePin={onTogglePin}
            onCopyLink={onCopyLink}
            onToggleReaction={onToggleReaction}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="group relative mt-1.5">
      {replyHeader}
      {contextMenu}
      <div className="flex gap-3 px-3 py-1.5 hover:bg-elevated/40 rounded-sm" onContextMenu={cm.open}>
        <UserClickTarget
          userId={message.author.id}
          username={message.author.username}
          displayName={message.author.displayName}
          avatarUrl={message.author.avatarUrl}
          className="shrink-0 hover:opacity-80 transition-opacity"
        >
          <Avatar name={displayName} url={message.author.avatarUrl} size={36} />
        </UserClickTarget>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <UserClickTarget
              userId={message.author.id}
              username={message.author.username}
              displayName={message.author.displayName}
              avatarUrl={message.author.avatarUrl}
              className={`message-author ${isMine ? 'self' : ''} text-sm hover:underline`}
            >
              {displayName}
            </UserClickTarget>
            <span className="text-[10px] text-text-subtle font-mono">{timeStr}</span>
            {message.editedAt && <span className="text-[10px] text-text-subtle italic">(edited)</span>}
            {message.pinned && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-accent" title="Pinned">
                <Icon name="pin" size={10} />
                Pinned
              </span>
            )}
          </div>
          <MessageContent message={message} />
          {reactions.length > 0 && (
            <MessageReactions
                messageId={message.id}
              reactions={reactions}
              onToggle={(emoji) => onToggleReaction(message.id, emoji)}
            />
          )}
        </div>
        <HoverActions
          message={message}
          isMine={isMine}
          canPin={canPin}
          onEdit={onEdit}
          onDelete={onDelete}
          onReply={onReply}
          onTogglePin={onTogglePin}
          onCopyLink={onCopyLink}
          onToggleReaction={onToggleReaction}
        />
      </div>
    </div>
  );
}

// =============================================================================
// MessageReactions — the row of chips below a message body.
// =============================================================================
// Each chip shows an emoji + count. Clicking a chip toggles your reaction
// (add if you haven't, remove if you have). Chips you've reacted with are
// styled with an accent border so you can see your own contribution at a
// glance.
//
// We also include a small "+" chip on the right that opens the picker —
// available to anyone hovering, not just the message author.
// =============================================================================
function MessageReactions({
  messageId,
  reactions,
  onToggle,
}: {
  messageId: string;
  reactions: Reaction[];
  onToggle: (emoji: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const plusBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-2">
      {reactions.map((r) => (
        <ReactionChip
          key={r.emoji}
          messageId={messageId}
          reaction={r}
          onClick={() => onToggle(r.emoji)}
        />
      ))}
      <button
        ref={plusBtnRef}
        type="button"
        onClick={() => setPickerOpen((o) => !o)}
        title="Add a reaction"
        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-elevated border border-border-soft text-text-muted hover:bg-elevated/70 hover:text-text transition-colors opacity-0 group-hover:opacity-100"
      >
        <Icon name="smile" size={12} />
        <span className="ml-0.5 text-[11px]">+</span>
      </button>
      {pickerOpen && (
        <ReactionPickerPopover
          anchor={plusBtnRef.current}
          onPick={(emoji) => {
            onToggle(emoji);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// =============================================================================
// ReactionChip — single emoji+count chip with hover-to-see-reactors tooltip.
// =============================================================================
// On hover, after a 350ms dwell, fetches the reactor list from the API and
// shows a small dark tooltip listing names. The dwell debounce avoids
// hammering the API when the cursor passes over a row of reactions.
//
// Hover tooltip closes immediately on mouseleave. We don't bother caching
// the result in state across hovers — the lookup is cheap and the data
// might change between hovers anyway.
// =============================================================================
function ReactionChip({
  messageId,
  reaction,
  onClick,
}: {
  messageId: string;
  reaction: Reaction;
  onClick: () => void;
}) {
  const [reactors, setReactors] = useState<Array<{ userId: string; username: string; displayName: string | null }> | null>(null);
  const [showTip, setShowTip] = useState(false);
  const dwellTimerRef = useRef<number | null>(null);
  const fetchedFor = useRef<string | null>(null);

  function onEnter() {
    if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = window.setTimeout(() => {
      setShowTip(true);
      // Only fetch if we haven't already for this (message, emoji) pair, OR
      // if the count has changed since last fetch (meaning the list shifted).
      const cacheKey = `${messageId}:${reaction.emoji}:${reaction.count}`;
      if (fetchedFor.current !== cacheKey) {
        fetchedFor.current = cacheKey;
        api
          .listReactors(messageId, reaction.emoji)
          .then((res) => setReactors(res.reactors))
          .catch(() => setReactors([]));
      }
    }, 350);
  }
  function onLeave() {
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
    setShowTip(false);
  }

  return (
    <span className="relative inline-block" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-colors ${
          reaction.mine
            ? 'bg-accent/20 border border-accent/60 text-text'
            : 'bg-elevated border border-border-soft text-text-muted hover:bg-elevated/70'
        }`}
      >
        <ReactionEmoji emoji={reaction.emoji} />
        <span className="font-mono text-xs">{reaction.count}</span>
      </button>
      {showTip && (
        <div
          role="tooltip"
          className="absolute bottom-full left-0 mb-1 z-50 px-2 py-1.5 rounded-aim bg-elevated border border-border shadow-aim-window whitespace-nowrap text-[11px] text-text pointer-events-none"
        >
          <div className="text-text-subtle text-[10px] uppercase tracking-wider font-semibold mb-0.5 flex items-center gap-1">
            <span>Reacted with</span>
            <ReactionEmoji emoji={reaction.emoji} />
          </div>
          {reactors === null ? (
            <div className="text-text-muted">Loading…</div>
          ) : reactors.length === 0 ? (
            <div className="text-text-muted">No one</div>
          ) : (
            <div className="space-y-0.5">
              {reactors.slice(0, 12).map((r) => (
                <div key={r.userId} className="text-text">
                  {r.displayName || r.username}
                </div>
              ))}
              {reactors.length > 12 && (
                <div className="text-text-subtle">+ {reactors.length - 12} more</div>
              )}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// =============================================================================
// ReactionPickerPopover — wraps EmojiPicker with auto-flip positioning.
// =============================================================================
// Anchored to the trigger element. Like UserPopover, it flips horizontally
// or vertically if the picker would overflow the viewport. Click-outside
// closes via mousedown listener so taps on the picker itself aren't
// interpreted as outside clicks.
// =============================================================================
function ReactionPickerPopover({
  anchor,
  onPick,
  onClose,
}: {
  anchor: HTMLElement | null;
  onPick: (emoji: string) => void;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // Pull custom emoji from the channel's server context so reactions can use
  // them. Empty in DMs (no server scope).
  const customEmojis = useContext(ServerEmojiContext);

  // Position relative to the anchor. Default: above the trigger; flip below
  // if there's no room above; pin to viewport otherwise.
  useEffect(() => {
    if (!anchor || !popoverRef.current) return;
    const a = anchor.getBoundingClientRect();
    const p = popoverRef.current.getBoundingClientRect();
    const margin = 8;
    let left = a.left;
    let top = a.top - p.height - margin;
    if (top < margin) top = a.bottom + margin; // not enough room above → go below
    if (left + p.width + margin > window.innerWidth) {
      left = window.innerWidth - p.width - margin;
    }
    if (left < margin) left = margin;
    if (top + p.height + margin > window.innerHeight) {
      top = Math.max(margin, window.innerHeight - p.height - margin);
    }
    setPos({ left, top });
  }, [anchor]);

  // Outside click to close. Bound on mousedown so it fires before any inner
  // click bubbles up.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target as Node)) return;
      if (anchor && anchor.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [anchor, onClose]);

  return (
    <div
      ref={popoverRef}
      className="fixed z-60"
      style={{
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <EmojiPicker onPick={onPick} onClose={onClose} customEmojis={customEmojis} />
    </div>
  );
}

// =============================================================================
// HoverActions — the floating toolbar on a message
// =============================================================================
function HoverActions({
  message,
  isMine,
  canPin,
  onEdit,
  onDelete,
  onReply,
  onTogglePin,
  onCopyLink,
  onToggleReaction,
}: {
  message: Message;
  isMine: boolean;
  canPin: boolean;
  onEdit: (m: Message) => void;
  onDelete: (m: Message) => void;
  onReply: (m: Message) => void;
  onTogglePin: (m: Message) => void;
  onCopyLink: (m: Message) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const reactBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <div className="absolute -top-3 right-3 hidden group-hover:flex items-center gap-0.5 p-0.5 bg-elevated border border-border-soft rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.12)] z-10">
        <button
          ref={reactBtnRef}
          onClick={() => setPickerOpen((o) => !o)}
          className="p-1.5 hover:bg-accent/20 rounded-full text-text-muted hover:text-text transition-colors"
          title="Add reaction"
        >
          <Icon name="smile" size={14} />
        </button>
        <button
          onClick={() => onReply(message)}
          className="p-1.5 hover:bg-accent/20 rounded-full text-text-muted hover:text-text transition-colors"
          title="Reply"
        >
          <Icon name="back" size={14} />
        </button>
        <button
          onClick={() => {
            onCopyLink(message);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
          className="p-1.5 hover:bg-accent/20 rounded-full text-text-muted hover:text-text transition-colors"
          title={copied ? 'Link copied!' : 'Copy link to message'}
        >
          <Icon name={copied ? 'check' : 'copy'} size={14} />
        </button>
        {canPin && (
          <button
            onClick={() => onTogglePin(message)}
            className="p-1.5 hover:bg-accent/20 rounded-full text-text-muted hover:text-text transition-colors"
            title={message.pinned ? 'Unpin message' : 'Pin message'}
          >
            <Icon name={message.pinned ? 'pin-off' : 'pin'} size={14} />
          </button>
        )}
        {isMine && (
          <>
            <button
              onClick={() => onEdit(message)}
              className="p-1.5 hover:bg-accent/20 rounded-full text-text-muted hover:text-text transition-colors"
              title="Edit"
            >
              <Icon name="edit" size={14} />
            </button>
            <button
              onClick={() => onDelete(message)}
              className="p-1.5 hover:bg-danger/20 rounded-full text-text-muted hover:text-danger transition-colors"
              title="Delete"
            >
              <Icon name="trash" size={14} />
            </button>
          </>
        )}
      </div>
      {pickerOpen && (
        <ReactionPickerPopover
          anchor={reactBtnRef.current}
          onPick={(emoji) => {
            onToggleReaction(message.id, emoji);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}

// =============================================================================
// MessageContent — text + attachments + mention highlighting
// =============================================================================
function MessageContent({ message }: { message: Message }) {
  const attachments = message.attachments ?? [];
  // Image attachments form a gallery — the lightbox lets you page across them
  // with arrows/keys. Hosted here (not per-Attachment) so it knows the full set.
  const images = attachments.filter((a) => a.contentType.startsWith('image/'));
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <>
      {message.content && (
        // `wrap-anywhere`, NOT `wrap-break-word`. Both wrap a long
        // token visually, but only `anywhere` reduces the element's MIN-CONTENT
        // width — and min-content is what a flex/grid parent sizes against. With
        // `break-word`, one long URL or token inflates this column past the
        // window, dragging the composer's Send button and the message hover
        // toolbar off-screen with it (the chat pane is `overflow-hidden`, so it
        // clips instead of scrolling and the controls simply vanish).
        <div className="text-sm text-text whitespace-pre-wrap wrap-anywhere">
          <FormattedText text={message.content} />
        </div>
      )}
      {attachments.length > 0 && (
        <div className="mt-1.5 space-y-1.5">
          {attachments.map((a, i) => (
            <Attachment
              key={a.id ?? i}
              att={a}
              onOpenImage={() => setLightboxIndex(images.indexOf(a))}
            />
          ))}
        </div>
      )}
      {message.embeds && message.embeds.length > 0 && (
        <MessageEmbeds embeds={message.embeds} />
      )}
      {lightboxIndex !== null && images[lightboxIndex] && (
        <ImageLightbox
          images={images}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

// =============================================================================
// MessageEmbeds — rich previews for URLs in messages
// =============================================================================
// Three render flavors keyed off the URL:
//
//   1. Direct image URLs (extension match)  → just inline the image, no chrome
//   2. YouTube / Vimeo                       → thumbnail with play overlay,
//                                               click to swap in an iframe player
//   3. Everything else                       → OG card (image + title + description)
//
// Embeds are aligned to the left edge of the message body, capped at a
// reasonable max-width so they don't dominate the chat. Multiple embeds
// stack vertically.
// =============================================================================

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|avif|svg)(?:\?|#|$)/i;

function isDirectImageUrl(url: string): boolean {
  return IMAGE_EXTENSIONS.test(url);
}

interface YouTubeInfo {
  videoId: string;
}
function parseYouTube(url: string): YouTubeInfo | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '');
      if (/^[a-zA-Z0-9_-]{6,}$/.test(id)) return { videoId: id };
      return null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v');
        if (id && /^[a-zA-Z0-9_-]{6,}$/.test(id)) return { videoId: id };
      }
      const m = /^\/(?:embed|shorts|live)\/([a-zA-Z0-9_-]{6,})/.exec(u.pathname);
      if (m) return { videoId: m[1]! };
    }
    return null;
  } catch {
    return null;
  }
}

function MessageEmbeds({ embeds }: { embeds: LinkEmbed[] }) {
  return (
    <div className="mt-1.5 space-y-2 max-w-[480px]">
      {embeds.map((e) => {
        const yt = parseYouTube(e.url);
        if (yt) return <YouTubeEmbed key={e.url} embed={e} videoId={yt.videoId} />;
        if (isDirectImageUrl(e.url)) return <ImageEmbed key={e.url} url={e.url} />;
        return <GenericEmbed key={e.url} embed={e} />;
      })}
    </div>
  );
}

function ImageEmbed({ url }: { url: string }) {
  const isGif = isGifLike({ url });
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block">
      {isGif ? (
        // Raw GIF URL pasted into chat (Tenor, Giphy, etc). No backend
        // thumbnail exists — when out of view we show a transparent
        // pixel so the animation pauses but layout is preserved by the
        // browser's natural image sizing once it's loaded.
        <AnimatedGif
          src={url}
          thumbSrc={null}
          alt=""
          className="block max-w-full max-h-[360px] rounded-aim border border-border-soft"
        />
      ) : (
        <img
          src={url}
          alt=""
          loading="lazy"
          className="block max-w-full max-h-[360px] rounded-aim border border-border-soft"
        />
      )}
    </a>
  );
}

function YouTubeEmbed({ embed, videoId }: { embed: LinkEmbed; videoId: string }) {
  const [playing, setPlaying] = useState(false);
  // Prefer YouTube's official thumbnail if og:image isn't available, since OG
  // sometimes returns a tiny logo instead of the real thumbnail.
  const thumb = embed.imageUrl || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const title = embed.title || 'YouTube video';

  if (playing) {
    // nocookie variant — same player without setting tracking cookies on
    // the user's first visit.
    const src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
    return (
      <div className="rounded-aim border border-border-soft overflow-hidden bg-black">
        <div className="aspect-video">
          <iframe
            src={src}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        <EmbedFooter embed={embed} />
      </div>
    );
  }

  return (
    <div className="rounded-aim border border-border-soft overflow-hidden bg-elevated">
      <button
        type="button"
        onClick={() => setPlaying(true)}
        className="block w-full relative group"
        aria-label={`Play: ${title}`}
      >
        <div className="aspect-video bg-black">
          <img src={thumb} alt={title} loading="lazy" className="w-full h-full object-cover" />
        </div>
        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
          <div className="w-14 h-10 rounded-lg bg-red-600/90 flex items-center justify-center shadow-lg">
            {/* Play triangle */}
            <span
              className="block w-0 h-0 ml-1"
              style={{
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderLeft: '12px solid white',
              }}
            />
          </div>
        </div>
      </button>
      <EmbedFooter embed={embed} />
    </div>
  );
}

function GenericEmbed({ embed }: { embed: LinkEmbed }) {
  // If we have nothing useful to show, fall back to a plain link line.
  if (!embed.title && !embed.description && !embed.imageUrl) return null;

  return (
    <div className="rounded-aim border border-border-soft overflow-hidden bg-elevated">
      {embed.imageUrl && (
        <a
          href={embed.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-black"
        >
          <img
            src={embed.imageUrl}
            alt=""
            loading="lazy"
            className="block w-full max-h-[260px] object-cover"
            onError={(e) => {
              // If the og:image 404s or blocks hotlinking, hide the image
              // gracefully instead of showing a broken-image icon.
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </a>
      )}
      <EmbedFooter embed={embed} />
    </div>
  );
}

function EmbedFooter({ embed }: { embed: LinkEmbed }) {
  let host: string | null = null;
  try {
    host = new URL(embed.url).hostname.replace(/^www\./, '');
  } catch { /* leave null */ }
  return (
    <div className="px-3 py-2">
      {(embed.siteName || host) && (
        <div className="text-[10px] uppercase tracking-wider text-text-subtle font-semibold mb-0.5">
          {embed.siteName || host}
        </div>
      )}
      {embed.title && (
        <a
          href={embed.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-sm font-semibold text-link hover:underline leading-snug"
        >
          {embed.title}
        </a>
      )}
      {embed.description && (
        <div className="text-xs text-text-muted mt-1 leading-snug line-clamp-3">
          {embed.description}
        </div>
      )}
    </div>
  );
}

function Attachment({
  att,
  onOpenImage,
}: {
  att: { filename: string; contentType: string; sizeBytes: number; width: number | null; height: number | null; url: string; thumbUrl?: string | null };
  onOpenImage?: () => void;
}) {
  const isImage = att.contentType.startsWith('image/');
  const isVideo = att.contentType.startsWith('video/');
  const isAudio = att.contentType.startsWith('audio/');

  if (isImage) {
    // Compute a display size that won't reflow when the image loads.
    // We have the true intrinsic dimensions from the backend probe.
    // Constraints: max 400px wide and 400px tall in the message list.
    const MAX_W = 400;
    const MAX_H = 400;
    let displayW: number | undefined;
    let displayH: number | undefined;
    if (att.width && att.height) {
      const scale = Math.min(MAX_W / att.width, MAX_H / att.height, 1);
      displayW = Math.round(att.width * scale);
      displayH = Math.round(att.height * scale);
    }

    const previewSrc = att.thumbUrl || att.url;
    const isGif = isGifLike({ contentType: att.contentType, url: att.url });

    return (
      <>
        <button
          type="button"
          className="block max-w-[400px] cursor-zoom-in"
          onClick={() => onOpenImage?.()}
          aria-label={`Open ${att.filename}`}
        >
          {isGif ? (
            // GIFs auto-play but pause when scrolled out of view. We pass
            // the static WebP thumb (which the backend generates from the
            // GIF's first frame) as the placeholder shown before/after
            // visibility, so users see the right image even when the
            // animation isn't running.
            <AnimatedGif
              src={att.url}
              thumbSrc={att.thumbUrl ?? null}
              alt={att.filename}
              width={displayW}
              height={displayH}
              className="rounded-aim border border-border-soft max-h-[400px] max-w-full hover:opacity-90 transition-opacity"
              draggable={false}
            />
          ) : (
            <img
              src={previewSrc}
              alt={att.filename}
              // Reserving width+height attrs prevents layout shift while loading.
              // Use Tailwind for caps so the constraint matches our calc.
              width={displayW}
              height={displayH}
              className="rounded-aim border border-border-soft max-h-[400px] max-w-full hover:opacity-90 transition-opacity"
              loading="lazy"
              draggable={false}
            />
          )}
          <div className="text-[10px] text-text-subtle mt-0.5 text-left">{att.filename}</div>
        </button>
      </>
    );
  }
  if (isVideo) return <video src={att.url} controls className="rounded-aim border border-border-soft max-w-[400px] max-h-[400px]" />;
  if (isAudio) return <audio src={att.url} controls className="max-w-[400px]" />;
  return (
    <a
      href={att.url}
      target="_blank"
      rel="noopener noreferrer"
      download={att.filename}
      className="bevel-down inline-flex items-center gap-2 px-3 py-2 rounded-aim bg-elevated hover:bg-panel transition-colors max-w-[300px]"
    >
      <Icon name="download" size={16} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate text-text">{att.filename}</div>
        <div className="text-[10px] text-text-subtle">{formatBytes(att.sizeBytes)}</div>
      </div>
    </a>
  );
}

// =============================================================================
// ImageLightbox — full-screen image viewer
// =============================================================================
// Backdrop covers the whole viewport. Click anywhere outside the image (or
// on the explicit close button) to dismiss. Esc closes too. Body scroll
// locks while open so the page doesn't scroll behind the modal.
// =============================================================================
type LightboxImage = { url: string; filename: string };

function ImageLightbox({
  images,
  index,
  onIndex,
  onClose,
}: {
  images: LightboxImage[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const img = images[index]!;
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  const [zoomed, setZoomed] = useState(false);

  const go = (delta: number) => {
    const next = index + delta;
    if (next >= 0 && next < images.length) onIndex(next);
  };

  // Reset zoom whenever the displayed image changes.
  useEffect(() => setZoomed(false), [index]);

  // Esc-to-close, arrow keys to navigate, + body scroll lock.
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // index intentionally in deps so the key handler closes over the current one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, index, images.length]);

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/80 backdrop-blur-xs lightbox-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Viewing ${img.filename}`}
    >
      {/* Top bar — filename + counter + actions */}
      <div
        className="absolute top-3 left-3 right-3 flex items-center justify-between gap-3 text-white z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1 text-sm truncate drop-shadow-sm">
          {img.filename}
          {images.length > 1 && (
            <span className="ml-2 text-white/60 tabular-nums">
              {index + 1} / {images.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={img.url}
            target="_blank"
            rel="noopener noreferrer"
            download={img.filename}
            className="text-xs px-2.5 py-1 rounded-aim bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-xs"
          >
            Open original
          </a>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-aim bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-xs flex items-center justify-center text-lg leading-none"
          >
            ×
          </button>
        </div>
      </div>

      {/* Prev / next arrows — only when there's more than one image. */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            disabled={!hasPrev}
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label="Previous image"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-0 transition-all backdrop-blur-xs flex items-center justify-center text-white z-10"
          >
            <Icon name="chevron-left" size={22} />
          </button>
          <button
            type="button"
            disabled={!hasNext}
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            aria-label="Next image"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-0 transition-all backdrop-blur-xs flex items-center justify-center text-white z-10"
          >
            <Icon name="chevron-right" size={22} />
          </button>
        </>
      )}

      {/* Scroll container so a zoomed (natural-size) image can be panned. */}
      <div
        className={`absolute inset-0 flex items-center justify-center p-4 sm:p-12 ${
          zoomed ? 'overflow-auto' : 'overflow-hidden'
        }`}
        onClick={onClose}
      >
        {/* Click the image to toggle zoom (fit ↔ natural size). Stop propagation
            so it doesn't also close. */}
        <img
          src={img.url}
          alt={img.filename}
          onClick={(e) => {
            e.stopPropagation();
            setZoomed((z) => !z);
          }}
          draggable={false}
          className={
            zoomed
              ? 'max-w-none max-h-none cursor-zoom-out rounded-aim shadow-2xl'
              : 'max-w-full max-h-full object-contain cursor-zoom-in rounded-aim shadow-2xl'
          }
        />
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

// =============================================================================
// FormattedText — links, mentions, and inline markdown.
// =============================================================================
// Supports:
//   - URLs (auto-linked)
//   - @mentions (highlighted, won't trigger inside code)
//   - `inline code`
//   - ```fenced code blocks``` (multi-line)
//   - **bold**
//   - *italic* and _italic_
//   - ~~strikethrough~~
//
// Tokenizer is hand-rolled (no markdown lib). Reason: we want a small,
// dependency-free implementation tuned to chat — robust to mismatched
// asterisks, partial markup, etc., where a generic markdown parser would
// either bail or render half-baked HTML.
//
// Precedence: code (fenced + inline) protects its contents from any other
// formatting. Within non-code spans, markdown is applied recursively to
// the inside of bold/italic/strike so combinations like **_bold italic_**
// work.
// =============================================================================

interface Token {
  type: 'text' | 'url' | 'mention' | 'emoji' | 'code-inline' | 'code-block' | 'bold' | 'italic' | 'strike' | 'spoiler';
  value: string;
  // For nested types (bold/italic/strike), `children` holds the parsed inner
  // tokens — we recursively format those.
  children?: Token[];
}

function tokenizeMessage(text: string): Token[] {
  // First pass: extract fenced code blocks (```...```). These are atomic and
  // protect their contents. Multi-line allowed.
  const result: Token[] = [];
  const fenceRe = /```([\s\S]*?)```/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = fenceRe.exec(text)) !== null) {
    if (m.index > cursor) {
      result.push(...tokenizeInline(text.slice(cursor, m.index)));
    }
    result.push({ type: 'code-block', value: m[1] ?? '' });
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) {
    result.push(...tokenizeInline(text.slice(cursor)));
  }
  return result;
}

function tokenizeInline(text: string): Token[] {
  // Inline code is the next-highest precedence. Extract first.
  const result: Token[] = [];
  const codeRe = /`([^`\n]+)`/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = codeRe.exec(text)) !== null) {
    if (m.index > cursor) {
      result.push(...tokenizeUrlsAndMarkup(text.slice(cursor, m.index)));
    }
    result.push({ type: 'code-inline', value: m[1] ?? '' });
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) {
    result.push(...tokenizeUrlsAndMarkup(text.slice(cursor)));
  }
  return result;
}

function tokenizeUrlsAndMarkup(text: string): Token[] {
  // URL match: greedy up to whitespace or HTML brackets. Underscores,
  // asterisks, tildes etc. ARE valid URL characters and must be included
  // verbatim — excluding them here was the cause of links getting truncated
  // at the first underscore (and breaking the embed lookup downstream).
  //
  // We strip trailing punctuation that's almost always sentence-end, not
  // part of the URL: . , ! ? ; : and an unmatched closing paren. This
  // mirrors what the backend embed extractor does so frontend rendering
  // and backend embed fetching agree on URL boundaries.
  const result: Token[] = [];
  const urlRe = /\bhttps?:\/\/[^\s<>]+/gi;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = urlRe.exec(text)) !== null) {
    let url = m[0];
    let end = m.index + url.length;

    // Trim trailing punctuation. Loop because multiple may stack ("…visit https://x.com.")
    while (url.length > 0) {
      const last = url[url.length - 1]!;
      if ('.,!?;:'.includes(last)) {
        url = url.slice(0, -1);
        end -= 1;
        continue;
      }
      // Unmatched closing paren — strip. Common with Markdown link forms
      // and parenthetical sentences.
      if (last === ')' && !url.includes('(')) {
        url = url.slice(0, -1);
        end -= 1;
        continue;
      }
      break;
    }

    if (url.length === 0) {
      // Pathological — the whole match was just punctuation after http://.
      // Skip and move past.
      cursor = m.index + m[0].length;
      continue;
    }

    if (m.index > cursor) {
      result.push(...tokenizeMarkup(text.slice(cursor, m.index)));
    }
    result.push({ type: 'url', value: url });
    cursor = end;
  }
  if (cursor < text.length) {
    result.push(...tokenizeMarkup(text.slice(cursor)));
  }
  return result;
}

function tokenizeMarkup(text: string): Token[] {
  // Now mentions and inline emphasis. We walk character by character so we
  // can handle ** and __ vs * and _ cleanly. Each emphasis match must have
  // non-empty content and may not span a newline (chat messages are short;
  // multi-line bold tends to be a typo).
  //
  // Order tried at each position: bold (**), strike (~~), italic (* or _),
  // mention (@name), then plain.

  const segments: Token[] = [];
  let i = 0;
  let plainStart = 0;

  function flushPlain(end: number) {
    if (end > plainStart) {
      segments.push({ type: 'text', value: text.slice(plainStart, end) });
    }
  }

  while (i < text.length) {
    // Spoiler: ||text|| — hidden until clicked. Highest precedence so its
    // delimiters aren't mistaken for anything else; inner content is still
    // formatted (you can bold/italic inside a spoiler).
    if (text.startsWith('||', i)) {
      const close = text.indexOf('||', i + 2);
      if (close > i + 2 && !text.slice(i + 2, close).includes('\n')) {
        flushPlain(i);
        segments.push({
          type: 'spoiler',
          value: text.slice(i + 2, close),
          children: tokenizeMarkup(text.slice(i + 2, close)),
        });
        i = close + 2;
        plainStart = i;
        continue;
      }
    }

    // Bold: **text**
    if (text.startsWith('**', i)) {
      const close = text.indexOf('**', i + 2);
      if (close > i + 2 && !text.slice(i + 2, close).includes('\n')) {
        flushPlain(i);
        segments.push({
          type: 'bold',
          value: text.slice(i + 2, close),
          children: tokenizeMarkup(text.slice(i + 2, close)),
        });
        i = close + 2;
        plainStart = i;
        continue;
      }
    }

    // Strike: ~~text~~
    if (text.startsWith('~~', i)) {
      const close = text.indexOf('~~', i + 2);
      if (close > i + 2 && !text.slice(i + 2, close).includes('\n')) {
        flushPlain(i);
        segments.push({
          type: 'strike',
          value: text.slice(i + 2, close),
          children: tokenizeMarkup(text.slice(i + 2, close)),
        });
        i = close + 2;
        plainStart = i;
        continue;
      }
    }

    // Italic: *text* — but not if it's part of ** (handled above) or if the
    // surrounding chars suggest it's not meant as emphasis.
    if (text[i] === '*' && text[i + 1] !== '*') {
      // Look for closing single * on same line, not preceded by another *.
      let j = i + 1;
      let close = -1;
      while (j < text.length) {
        if (text[j] === '\n') break;
        if (text[j] === '*' && text[j + 1] !== '*' && text[j - 1] !== '*') {
          close = j;
          break;
        }
        j++;
      }
      if (close > i + 1) {
        flushPlain(i);
        segments.push({
          type: 'italic',
          value: text.slice(i + 1, close),
          children: tokenizeMarkup(text.slice(i + 1, close)),
        });
        i = close + 1;
        plainStart = i;
        continue;
      }
    }

    // Italic: _text_ — only when underscore is at a word boundary. Without
    // this guard, `some_var_name` would render `var` as italicized.
    if (text[i] === '_') {
      const before = text[i - 1];
      const isStart = i === 0 || /\s|[(\[{]/.test(before ?? '');
      if (isStart) {
        let j = i + 1;
        let close = -1;
        while (j < text.length) {
          if (text[j] === '\n') break;
          if (text[j] === '_') {
            const after = text[j + 1];
            const isEnd = j === text.length - 1 || /\s|[)\]}.,!?:;]/.test(after ?? '');
            if (isEnd) {
              close = j;
              break;
            }
          }
          j++;
        }
        if (close > i + 1) {
          flushPlain(i);
          segments.push({
            type: 'italic',
            value: text.slice(i + 1, close),
            children: tokenizeMarkup(text.slice(i + 1, close)),
          });
          i = close + 1;
          plainStart = i;
          continue;
        }
      }
    }

    // Mention: @name (with leading whitespace OR start of segment)
    if (text[i] === '@') {
      const isStart = i === 0 || /\s/.test(text[i - 1] ?? '');
      if (isStart) {
        const tail = text.slice(i + 1).match(/^[a-zA-Z0-9_.\-]{2,32}/);
        if (tail) {
          flushPlain(i);
          segments.push({ type: 'mention', value: '@' + tail[0] });
          i += 1 + tail[0].length;
          plainStart = i;
          continue;
        }
      }
    }

    // Custom emoji: :name: — the name must be 2-32 chars of [a-z0-9_],
    // followed by a closing colon. Anchored match only — partial names
    // like ":foo" without a closing colon stay as plain text.
    //
    // We tokenize unconditionally — if the name doesn't actually exist in
    // the current server's emoji set (or we're in a DM), the renderer
    // falls back to the literal `:name:` text. That way edits on the
    // server (renames, deletes) don't break old messages: they revert to
    // text gracefully.
    if (text[i] === ':') {
      const tail = text.slice(i + 1).match(/^([a-z0-9_]{2,32}):/);
      if (tail) {
        flushPlain(i);
        segments.push({ type: 'emoji', value: tail[1]! });
        i += 2 + tail[1]!.length; // ':' + name + ':'
        plainStart = i;
        continue;
      }
    }

    i++;
  }
  flushPlain(text.length);
  return segments;
}

function FormattedText({ text }: { text: string }) {
  const tokens = useMemo(() => tokenizeMessage(text), [text]);
  return <RenderTokens tokens={tokens} />;
}

function RenderTokens({ tokens }: { tokens: Token[] }) {
  // Custom emoji lookup — provided by ServerEmojiContext at the channel level.
  // For DMs the context default (empty array) means all :name: tokens fall
  // back to literal text gracefully.
  const customEmojis = useContext(ServerEmojiContext);
  const emojiByName = useMemo(() => {
    const m = new Map<string, ServerEmoji>();
    for (const e of customEmojis) m.set(e.name, e);
    return m;
  }, [customEmojis]);
  return (
    <>
      {tokens.map((t, i) => {
        if (t.type === 'url') {
          return (
            <a
              key={i}
              href={t.value}
              target="_blank"
              rel="noopener noreferrer"
              className="aim-link wrap-anywhere"
            >
              {t.value}
            </a>
          );
        }
        if (t.type === 'mention') {
          return (
            <span
              key={i}
              className="inline-block bg-accent/30 text-accent rounded-sm px-1 -my-0.5 font-semibold cursor-default"
            >
              {t.value}
            </span>
          );
        }
        if (t.type === 'emoji') {
          // Custom server emoji. If we don't have it (different server, deleted,
          // unknown name) fall back to literal `:name:` text — graceful, doesn't
          // break message readability. Vertical-align middle keeps it aligned
          // with surrounding text baseline.
          const found = emojiByName.get(t.value);
          if (!found) {
            return <span key={i}>:{t.value}:</span>;
          }
          return (
            <img
              key={i}
              src={found.url}
              alt={`:${found.name}:`}
              title={`:${found.name}:`}
              draggable={false}
              className="inline-block align-middle h-[28px] w-[28px] -my-1.5 select-none"
              loading="lazy"
            />
          );
        }
        if (t.type === 'code-inline') {
          return (
            <code
              key={i}
              // Was `whitespace-nowrap`, which made a long inline `token`
              // literally unbreakable and blew out the whole chat column.
              // pre-wrap keeps internal spacing; anywhere lets it break.
              className="font-mono text-[0.85em] bg-elevated border border-border-soft rounded-sm px-1 py-px whitespace-pre-wrap wrap-anywhere"
            >
              {t.value}
            </code>
          );
        }
        if (t.type === 'code-block') {
          // Preserve newlines and leading whitespace; render in a block.
          // Strip a leading newline that follows the opening ``` for cleaner
          // appearance ("```\nfoo" -> "foo" not "\nfoo").
          const stripped = t.value.replace(/^\n/, '').replace(/\n$/, '');
          return (
            <pre
              key={i}
              className="font-mono text-[0.85em] bg-elevated border border-border-soft rounded-sm p-2 my-1 overflow-x-auto whitespace-pre"
            >
              <code>{stripped}</code>
            </pre>
          );
        }
        if (t.type === 'bold') {
          return (
            <strong key={i} className="font-bold">
              {t.children ? <RenderTokens tokens={t.children} /> : t.value}
            </strong>
          );
        }
        if (t.type === 'italic') {
          return (
            <em key={i} className="italic">
              {t.children ? <RenderTokens tokens={t.children} /> : t.value}
            </em>
          );
        }
        if (t.type === 'strike') {
          return (
            <span key={i} className="line-through opacity-80">
              {t.children ? <RenderTokens tokens={t.children} /> : t.value}
            </span>
          );
        }
        if (t.type === 'spoiler') {
          return (
            <Spoiler key={i}>
              {t.children ? <RenderTokens tokens={t.children} /> : t.value}
            </Spoiler>
          );
        }
        return <span key={i}>{t.value}</span>;
      })}
    </>
  );
}

// Spoiler — click-to-reveal blurred text (Discord-style ||spoiler||). Hidden
// content is blacked out + blurred until the user clicks it. Once revealed it
// stays revealed for the session.
function Spoiler({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={revealed ? undefined : 'Spoiler, click to reveal'}
      onClick={(e) => {
        if (!revealed) {
          e.stopPropagation();
          setRevealed(true);
        }
      }}
      onKeyDown={(e) => {
        if (!revealed && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          setRevealed(true);
        }
      }}
      className={
        revealed
          ? 'rounded-sm bg-elevated/60 px-0.5'
          : 'rounded-sm bg-text/85 text-transparent px-0.5 cursor-pointer select-none filter-[blur(0.3px)] hover:bg-text/75 transition-colors'
      }
    >
      {children}
    </span>
  );
}

// =============================================================================
// ComposerEmojiButton — smile-icon button next to the textarea that opens
// the emoji picker as a popover anchored above the composer.
// =============================================================================
// Picking an emoji calls the parent's onPick (which handles cursor-position
// insertion in the composer) and closes the popover. Outside-click and Esc
// also close. Disabled while editing/sending — the surrounding controls in
// those states already block interaction so the emoji button matches.
// =============================================================================
function ComposerEmojiButton({
  onPick,
  disabled,
}: {
  onPick: (emoji: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // Pull current server's custom emoji so the composer picker shows them
  // alongside unicode emoji.
  const customEmojis = useContext(ServerEmojiContext);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null;
      if (popRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Add emoji"
        className={COMPOSER_ICON_BTN}
        disabled={disabled}
      >
        <Icon name="smile" size={18} />
      </button>
      {open && (
        <div
          ref={popRef}
          // Anchor to the left above the composer (the button now lives in
          // the left utility cluster) so it doesn't push the chat content.
          // z-50 to layer over message rows.
          className="absolute left-0 bottom-full mb-2 z-50"
        >
          <EmojiPicker
            onPick={(emoji) => {
              onPick(emoji);
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
            customEmojis={customEmojis}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ComposerGifButton — GIF-icon button next to the emoji button that opens
// the Giphy picker. Clicking a GIF posts it directly to the channel.
// =============================================================================
// The picker handles its own loading + error UI. This button just handles:
//   - toggle popover visibility + outside-click + Esc
//   - relay the post call to the API (server downloads + creates message)
//   - replyToId pass-through so a GIF posted while a reply is active
//     becomes the reply itself (matches text-message behavior)
// On success the popover closes (the picker calls onClose after onPick
// resolves). The new message arrives via the standard WS path.
// =============================================================================
function ComposerGifButton({
  onAttach,
  disabled,
}: {
  /** Called when the user picks a GIF. Resolves with the staged storage
   *  metadata so the parent can add it to the pending-attachment list. */
  onAttach: (gif: { fullUrl: string }) => Promise<void>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null;
      if (popRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Pick a GIF"
        className={`${COMPOSER_ICON_BTN} font-bold tracking-tight`}
        disabled={disabled}
        aria-label="Pick a GIF"
      >
        {/* Text label rather than an icon — "GIF" is more recognizable
            than any one-glyph icon for this control, matches Discord/Slack.
            Sized to read as the same visual weight as the 18px sibling
            icons inside the shared 32px square. */}
        <span className="text-[11px] leading-none">GIF</span>
      </button>
      {open && (
        <div
          ref={popRef}
          // Anchor left to match the relocated utility cluster.
          className="absolute left-0 bottom-full mb-2 z-50"
        >
          <GifPicker
            onPick={async (gif) => {
              // Stage the GIF (server downloads + uploads to MinIO),
              // then attach it to the composer. Picker auto-closes
              // after onPick resolves.
              await onAttach({ fullUrl: gif.fullUrl });
            }}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ReactionEmoji — render an emoji that may be either a unicode character or
// a custom shortcode like ":pepe:".
// =============================================================================
// Used in the reaction chips and tooltip. Looks up the shortcode against
// the current server's emoji set via context. If it doesn't resolve (other
// server, deleted), falls back to the literal text.
// =============================================================================
function ReactionEmoji({ emoji }: { emoji: string }) {
  const customEmojis = useContext(ServerEmojiContext);
  // Match :name: shape — same regex as the message tokenizer.
  const m = /^:([a-z0-9_]{2,32}):$/.exec(emoji);
  if (m) {
    const found = customEmojis.find((e) => e.name === m[1]);
    if (found) {
      return (
        <img
          src={found.url}
          alt={emoji}
          title={emoji}
          draggable={false}
          className="inline-block align-middle h-[22px] w-[22px] select-none"
          loading="lazy"
        />
      );
    }
    // Custom shortcode we don't know — show literal as fallback.
    return <span className="text-base leading-none">{emoji}</span>;
  }
  return <span className="text-base leading-none">{emoji}</span>;
}

// =============================================================================
// PendingAttachmentsStrip — thumbnails for files queued for the next send.
// =============================================================================
// Renders horizontally above the composer. Each entry shows either an image
// thumbnail (for image files where we built an object URL) or a file-icon
// placeholder + filename. An × in the corner removes the attachment from
// the queue. The MinIO object stays uploaded — we don't delete it on
// remove. (Unused uploads get reaped by the existing storage cleanup.)
// =============================================================================
function PendingAttachmentsStrip({
  attachments,
  onRemove,
}: {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-2 overflow-x-auto py-1">
      {attachments.map((a) => (
        <div
          key={a.id}
          className="relative shrink-0 group"
          title={a.filename}
        >
          {a.previewUrl ? (
            <img
              src={a.previewUrl}
              alt={a.filename}
              className="h-16 w-16 object-cover rounded-aim border border-border-soft"
              draggable={false}
            />
          ) : (
            <div className="h-16 w-32 rounded-aim border border-border-soft bg-bg flex flex-col items-center justify-center px-2">
              <Icon name="paperclip" size={14} className="text-text-muted mb-0.5" />
              <div className="text-[10px] text-text-muted truncate w-full text-center">
                {a.filename}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(a.id)}
            title="Remove"
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-bg border border-border text-text shadow-aim-window flex items-center justify-center text-xs leading-none hover:bg-danger hover:text-white hover:border-danger transition-colors"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Channel search — header search box with a Discord-style filter dropdown.
// Parses `from:` / `has:link|file|image` tokens out of the raw input; the
// remaining words are the free-text query. Scoped to the current channel/DM.
// =============================================================================
type SearchFilters = { text?: string; from?: string; has?: 'link' | 'file' | 'image' };

function parseSearchInput(raw: string): SearchFilters {
  let from: string | undefined;
  let has: 'link' | 'file' | 'image' | undefined;
  const words: string[] = [];
  for (const tok of raw.split(/\s+/)) {
    if (!tok) continue;
    const lower = tok.toLowerCase();
    if (lower.startsWith('from:') && tok.length > 5) {
      from = tok.slice(5);
    } else if (lower.startsWith('has:')) {
      const v = lower.slice(4);
      if (v === 'link' || v === 'file' || v === 'image') has = v;
    } else {
      words.push(tok);
    }
  }
  const text = words.join(' ').trim() || undefined;
  return { text, from, has };
}

function ChannelSearch({
  members,
  active,
  onSearch,
  onClear,
}: {
  members: Member[];
  active: boolean;
  onSearch: (p: SearchFilters) => void;
  onClear: () => void;
}) {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Dismiss the dropdown on outside click.
  useEffect(() => {
    if (!focused) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setFocused(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [focused]);

  function submit() {
    const parsed = parseSearchInput(value);
    if (!parsed.text && !parsed.from && !parsed.has) return;
    setFocused(false);
    onSearch(parsed);
  }

  function clear() {
    setValue('');
    onClear();
  }

  // When the last token is a partial `from:`, suggest matching members.
  const lastToken = value.split(/\s+/).pop() ?? '';
  const fromQuery = lastToken.toLowerCase().startsWith('from:')
    ? lastToken.slice(5).toLowerCase()
    : null;
  const memberMatches =
    fromQuery !== null
      ? members
          .filter(
            (m) =>
              (m.displayName || m.username).toLowerCase().includes(fromQuery) ||
              m.username.toLowerCase().includes(fromQuery)
          )
          .slice(0, 6)
      : [];

  function insertToken(token: string) {
    const parts = value.split(/\s+/);
    parts[parts.length - 1] = token;
    setValue(parts.join(' ') + ' ');
    inputRef.current?.focus();
  }

  return (
    <div ref={wrapRef} className="relative">
      <div
        className={`flex items-center gap-1.5 rounded-aim bevel-down bg-elevated px-2 h-7 transition-all ${
          active || value ? 'w-52' : 'w-36 focus-within:w-52'
        }`}
      >
        <Icon name="search" size={12} className="text-text-subtle shrink-0" />
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              setFocused(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Search"
          className="flex-1 min-w-0 bg-transparent outline-hidden text-xs text-text"
        />
        {(value || active) && (
          <button
            type="button"
            onClick={clear}
            className="text-text-subtle hover:text-text text-sm leading-none shrink-0"
            title="Clear search" aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {focused && (
        <div className="absolute right-0 top-full mt-1 w-64 hybrid-card z-30 animate-slide-up overflow-hidden">
          {fromQuery !== null && memberMatches.length > 0 ? (
            <>
              <div className="hybrid-card-header py-1! text-[11px]!">From user</div>
              <div className="bg-surface">
                {memberMatches.map((m) => {
                  const name = m.displayName || m.username;
                  return (
                    <button
                      key={m.userId}
                      type="button"
                      onClick={() => insertToken(`from:${m.username}`)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-elevated text-left"
                    >
                      <Avatar name={name} url={m.avatarUrl} size={18} />
                      <span className="text-xs font-medium text-text">{name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="hybrid-card-header py-1! text-[11px]!">Search filters</div>
              <div className="bg-surface py-0.5">
                <FilterHint label="from:" desc="messages from a user" onClick={() => insertToken('from:')} />
                <FilterHint label="has:link" desc="messages containing links" onClick={() => insertToken('has:link')} />
                <FilterHint label="has:file" desc="messages with attachments" onClick={() => insertToken('has:file')} />
                <FilterHint label="has:image" desc="messages with images" onClick={() => insertToken('has:image')} />
              </div>
              <div className="px-3 py-1.5 text-[10px] text-text-subtle border-t border-border-soft bg-surface">
                Enter to search · Esc to dismiss
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FilterHint({ label, desc, onClick }: { label: string; desc: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-elevated text-left"
    >
      <span className="text-xs font-mono font-semibold text-accent bg-accent/15 rounded-sm px-1.5 py-0.5">
        {label}
      </span>
      <span className="text-[11px] text-text-muted">{desc}</span>
    </button>
  );
}

// =============================================================================
// SearchResultsSidebar — right-hand panel listing channel search matches.
// =============================================================================
function SearchResultsSidebar({
  query,
  results,
  loading,
  hasMore,
  error,
  onJump,
  onClose,
  onLoadMore,
}: {
  query: SearchFilters;
  results: Message[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  onJump: (id: string) => void;
  onClose: () => void;
  onLoadMore: () => void;
}) {
  const chips: string[] = [];
  if (query.from) chips.push(`from: ${query.from}`);
  if (query.has) chips.push(`has: ${query.has}`);

  return (
    <div className="w-80 max-w-[80vw] shrink-0 border-l border-border bg-surface flex flex-col min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
        <Icon name="search" size={13} className="text-text-subtle" />
        <span className="text-sm font-bold text-text flex-1">
          {loading && results.length === 0
            ? 'Searching…'
            : `${results.length}${hasMore ? '+' : ''} result${results.length === 1 ? '' : 's'}`}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-text-subtle hover:text-text text-lg leading-none"
          title="Close search" aria-label="Close search"
        >
          ×
        </button>
      </div>

      {(query.text || chips.length > 0) && (
        <div className="px-3 py-1.5 flex flex-wrap items-center gap-1 border-b border-border-soft shrink-0">
          {query.text && <span className="text-xs text-text-muted">“{query.text}”</span>}
          {chips.map((c) => (
            <span key={c} className="text-[10px] font-mono text-accent bg-accent/15 rounded-sm px-1.5 py-0.5">
              {c}
            </span>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
        {error && <div className="text-danger text-xs p-3">{error}</div>}
        {!loading && !error && results.length === 0 && (
          <div className="text-center text-sm text-text-muted py-8 px-3">No messages found.</div>
        )}
        <div className="divide-y divide-border-soft">
          {results.map((m) => (
            <SearchResultRow key={m.id} message={m} onJump={() => onJump(m.id)} />
          ))}
        </div>
        {hasMore && (
          <div className="p-2">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loading}
              className="w-full text-xs text-link hover:underline py-1.5 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SearchResultRow({ message, onJump }: { message: Message; onJump: () => void }) {
  const name = message.author.displayName || message.author.username;
  const time = new Date(message.createdAt);
  const dateStr =
    time.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' · ' +
    time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const hasAttachments = !!message.attachments && message.attachments.length > 0;

  return (
    <button
      type="button"
      onClick={onJump}
      className="w-full text-left px-3 py-2 hover:bg-elevated/60 block group/result"
      title="Jump to message" aria-label="Jump to message"
    >
      <div className="flex items-center gap-2 mb-0.5">
        <Avatar name={name} url={message.author.avatarUrl} size={18} />
        <span className="text-xs font-semibold text-text">{name}</span>
        <span className="text-[10px] text-text-subtle ml-auto">{dateStr}</span>
      </div>
      <div className="text-xs text-text-muted line-clamp-3 whitespace-pre-wrap wrap-anywhere">
        {message.content || (hasAttachments ? '📎 Attachment' : '')}
      </div>
      <div className="text-[10px] text-link opacity-0 group-hover/result:opacity-100 mt-1">Jump →</div>
    </button>
  );
}

// =============================================================================
// PinsButton — header button + dropdown listing the channel's pinned messages.
// =============================================================================
// Fetches /pins on open (live timeline updates already arrive via the
// message.pinned/unpinned WS events). Positioned fixed, right-aligned under the
// trigger so it escapes the header's overflow — same approach as AddServerMenu.
function PinsButton({
  channelId,
  canPin,
  onJump,
}: {
  channelId: string;
  canPin: boolean;
  onJump: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pins, setPins] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.getChannelPins(channelId);
      setPins(res.messages);
    } catch {
      setPins([]);
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    if (!open) {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setPos({ left: Math.max(8, r.right - 320), top: r.bottom + 6 });
      void load();
    }
    setOpen((o) => !o);
  }

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function unpin(id: string) {
    setPins((prev) => (prev ? prev.filter((m) => m.id !== id) : prev));
    try {
      await api.unpinMessage(id);
    } catch {
      void load(); // restore on failure
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        title="Pinned messages" aria-label="Pinned messages"
        className="h-8 w-8 inline-flex items-center justify-center rounded-aim text-text-subtle hover:text-text hover:bg-elevated transition-colors"
      >
        <Icon name="pin" size={16} />
      </button>
      {open && pos && (
        <div
          ref={panelRef}
          className="fixed z-60 hybrid-card w-[320px] max-h-[60vh] flex flex-col"
          style={{ left: pos.left, top: pos.top }}
        >
          <div className="hybrid-card-header py-1.5! flex items-center gap-1.5">
            <Icon name="pin" size={12} />
            <span>Pinned Messages</span>
          </div>
          <div className="bg-surface flex-1 overflow-y-auto min-h-0">
            {loading && (
              <div className="p-4 flex items-center justify-center text-text-subtle">
                <Spinner size={14} />
              </div>
            )}
            {!loading && pins && pins.length === 0 && (
              <div className="p-4 text-center text-xs text-text-subtle">
                No pinned messages yet.
              </div>
            )}
            {!loading &&
              pins &&
              pins.map((m) => (
                <PinnedRow
                  key={m.id}
                  message={m}
                  canPin={canPin}
                  onJump={() => {
                    onJump(m.id);
                    setOpen(false);
                  }}
                  onUnpin={() => void unpin(m.id)}
                />
              ))}
          </div>
        </div>
      )}
    </>
  );
}

function PinnedRow({
  message,
  canPin,
  onJump,
  onUnpin,
}: {
  message: Message;
  canPin: boolean;
  onJump: () => void;
  onUnpin: () => void;
}) {
  const name = message.author.displayName || message.author.username;
  const time = new Date(message.createdAt);
  const dateStr =
    time.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' · ' +
    time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const hasAttachments = !!message.attachments && message.attachments.length > 0;

  return (
    <div className="relative group/pin border-b border-border-soft last:border-b-0">
      <button
        type="button"
        onClick={onJump}
        className="w-full text-left px-3 py-2 hover:bg-elevated/60 block"
        title="Jump to message" aria-label="Jump to message"
      >
        <div className="flex items-center gap-2 mb-0.5">
          <Avatar name={name} url={message.author.avatarUrl} size={18} />
          <span className="text-xs font-semibold text-text">{name}</span>
          <span className="text-[10px] text-text-subtle ml-auto pr-6">{dateStr}</span>
        </div>
        <div className="text-xs text-text-muted line-clamp-3 whitespace-pre-wrap wrap-anywhere">
          {message.content || (hasAttachments ? '📎 Attachment' : '')}
        </div>
      </button>
      {canPin && (
        <button
          type="button"
          onClick={onUnpin}
          title="Unpin" aria-label="Unpin"
          className="absolute top-2 right-2 p-1 rounded-full text-text-subtle hover:text-danger hover:bg-danger/15 opacity-0 group-hover/pin:opacity-100 transition-opacity"
        >
          <Icon name="pin-off" size={13} />
        </button>
      )}
    </div>
  );
}
